import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  aushang as ladeAushang,
  leeren as leereBelegung,
  trainerplan as ladeTrainerplan,
  ressourceEntfernen,
  ressourceSetzen,
  ressourcenvorschlag as ladeVorschlag,
  ressourcenZuteilen as teileRessourcenZu,
  ablaufAnwenden,
  setzen as setzeBaustein,
  standSetzen,
  standorte as ladeStandorte,
  offeneTage as ladeOffeneTage,
  STANDNAME,
  RUECKMELDUNGSNAME,
  tage as ladeTage,
  tagesplan as ladeTagesplan,
  tauschen as tausche,
  verschieben as verschiebe,
  type Bedarfsdeckung,
  type Belegung,
  type Belegungsressource,
  type OffeneTageJeStandort,
  type Planungsstandort,
  type Ressourcenvorschlag,
  type Tagesplan,
  type Terminbedarf,
  type Terminbelegung,
  type Tageszeile,
  type Trainingszeile,
  type Planungsstand,
  type Zuteilung,
} from '../api/tagesplanung';
import { ApiFehler } from '../api/fehler';
import { alsTag } from '../datum';
import { AbwaehlbareMarke } from '../komponenten/Filtermarken';
import { Meldung } from '../komponenten/Meldung';
import { Planung } from '../komponenten/Planung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Segmente } from '../komponenten/Segmente';
import { AbTablet } from '../komponenten/AbTablet';
import { ladeUndReiche } from '../komponenten/datei';
import { useTelefon } from '../komponenten/telefon';
import { Terminfenster } from '../termin/Terminfenster';
import { OffenePlanungen } from './OffenePlanungen';
import { Tageskarten } from './Tageskarten';
import {
  SetzWahl,
  Tagesraster,
  Terminkopf,
  rasterzeilen,
  type Abschnitt,
  type Terminspalte,
} from './Tagesraster';

/**
 * Die Trainingsplanung (TAG-03, TAG-09, TAG-13, AeA-08).
 *
 * ============================================================================
 * DER TAG IST DER GEGENSTAND, NICHT DER KURS
 * ============================================================================
 *
 * Der Auftraggeber hat es am 07.09.2026 so gefasst: „Ein Tag besteht aus
 * verschiedenen Trainings und verschiedenen Anfangszeiten." Genau deshalb ist
 * das hier ein eigener Menüpunkt und kein Reiter am Produkt: Wer den Tag
 * plant, denkt nicht in Programmen, sondern in Uhrzeiten und Modulen.
 *
 * Oben stehen die Trainings mit ihren Anfangszeiten — die Liste, nach der
 * gefragt wurde. Darunter das Raster aus Blöcken und Gruppen (TAG-03), über
 * ALLE Trainings des Tages hinweg.
 *
 * ============================================================================
 * DIE LEEREN ZELLEN GEHÖREN DAZU
 * ============================================================================
 *
 * Ein Raster, in dem nur die gefüllten Zellen stehen, ist keines: Wer sucht,
 * wo noch Platz ist, findet ihn nicht in einer Liste des Belegten. Die
 * Vorschau zeigt deshalb jeden Block mal jede Gruppe.
 *
 * ============================================================================
 * WAS NICHT GEHT, STEHT DA, BEVOR JEMAND ES VERSUCHT
 * ============================================================================
 *
 * Ein Training, dessen Beginn auf keinen Block des Tages fällt, lässt sich
 * nicht einplanen (0136). Ein Produkt ohne Trainingsdefinition hat nichts
 * anzuordnen (PRD-29). Beides steht in der Trainingsliste — nicht in der
 * Fehlermeldung nach dem Klick.
 *
 * ============================================================================
 * DER ABLAUF KOMMT AUS DEN BAUSTEINEN (AeA-11)
 * ============================================================================
 *
 * „Der Ablauf eines Trainings definiert sich aus den Bausteinen und den
 * Phasen" (Auftraggeber, 08.09.2026). Es gibt deshalb nichts mehr zu wählen:
 * Der Knopf ordnet DIESES Programm an diesem Tag an. Was dabei auffällt,
 * steht als Hinweis daneben und verhindert nichts (TAG-20).
 *
 * ============================================================================
 * DIE ROTATION IST KEINE FUNKTION, ABER EIN GRIFF (AeA-12, AeA-17)
 * ============================================================================
 *
 * „Die Rotation ist nur eine Option bei der Planung ... daher wird sie nicht
 * als Einzelfunktion benötigt" (Auftraggeber, 08.09.2026, AeA-12). Dabei
 * bleibt es: Es gibt keinen Knopf „rotieren" und kein Rotationsschema.
 *
 * Alle Termine eines Laufs bekommen zunächst denselben Ablauf — und damit
 * verlangen drei gleichzeitige Gruppen dasselbe Modul zur selben Zeit. Seit
 * AeA-17 löst das die RESSOURCENZUTEILUNG: Bevor sie sucht, dreht sie die
 * Stationen einer Phase je Termin gegeneinander, solange die Phase ihre Lage
 * behält und niemand von Hand eingegriffen hat. Was sie gedreht hat, steht
 * im Bericht — eine Änderung am Plan bleibt nicht still (TAG-08).
 *
 * Von Hand geht weiterhin alles: Die Zellen lassen sich tauschen und
 * verschieben, und was so steht, rührt der Automat nicht mehr an.
 *
 * ============================================================================
 * ZWEI ANSICHTEN DESSELBEN TAGES (13.09.2026)
 * ============================================================================
 *
 * Der Auftraggeber: „in der trainingsplanung sind die bausteine im tagesplan
 * sehr gestaucht, baue eine ansicht, in der man den inhalt der felder besser
 * lesen und sie verschieben kann."
 *
 * Auf der Zeitachse bestimmt die DAUER die Breite — eine Station von 45 Minuten
 * bekommt auf einem Zehnstundentag 7,5 % der Bahn, und darin steht kein Satz.
 * Das Raster (`Tagesraster.tsx`) dreht die Achse nach unten und gibt jeder
 * Station die ganze Spalte. Beide Ansichten lesen dasselbe Modell und teilen
 * denselben Zustand: Was in der einen angefasst ist, ist es in der anderen.
 *
 * KEINE ANFORDERUNG IST BETROFFEN. ÄA-09 hat es selbst festgehalten: „TAG-03
 * verlangt ein Raster aus Blöcken und Ressourcen und nicht eine bestimmte
 * Tabellenform."
 */

/** Heute, als `JJJJ-MM-TT` in Ortszeit. */
function heute(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function inTagen(von: string, tage: number): string {
  const d = new Date(`${von}T12:00:00`);
  d.setDate(d.getDate() + tage);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Welche der beiden Darstellungen des Tages gilt.
 *
 * Die Zeitachse ist die Vorbelegung: Sie zeigt den Tag am Stück, und darum hat
 * ÄA-09 sie verlangt. Das Raster ist die lesbare Ansicht für die Arbeit an den
 * Feldern — beide zeigen dieselben Daten und arbeiten auf demselben Zustand.
 */
type Ansicht = 'zeitachse' | 'raster';

/*
 * DIE RÜCKFRAGE IM POP-UP (Auftrag CRM-104).
 *
 * Was den Tag weiterschaltet, ohne dass ein Weg zurückführt, und was Planung
 * überschreibt oder löscht, fragt erst nach. Offen ist höchstens eine.
 *
 * Die Handgriffe im Raster fragen NICHT: Setzen, Verschieben, Tauschen und das
 * Zuteilen oder Wegnehmen einer Ressource sind die laufende Nachbearbeitung
 * nach TAG-08 — eine Rückfrage je Zug machte das Planen unbedienbar. Ebenso
 * wenig „Tag freigeben": Die Freigabe nimmt „Freigabe zurücknehmen" zurück,
 * und was sie hindert, steht vorher daneben (TAG-24).
 */
type Nachfrage =
  | { readonly art: 'stand'; readonly neuer: 'entwurf' | 'in_durchfuehrung' | 'abgeschlossen' }
  | { readonly art: 'ersetzen'; readonly training: Trainingszeile }
  | { readonly art: 'leeren'; readonly belegung: Belegung };

export function Trainingsplanung({
  tag: einstiegstag,
  standort: einstiegsort,
  beiTag,
  darfPlanen: darfPlanenRecht,
  darfFreigeben,
  darfAbsagen,
  darfVereinbarungAendern = false,
  darfWartelisteSehen = false,
  beiTerminakte,
  darfOptionBeantworten,
}: {
  /**
   * DER EINSTIEG MIT TAG (TAG-29, Auftrag CRM-67): Aus den Terminen springt
   * das Datum hierher, und dieser Tag geht auf statt des heutigen. Der
   * Zeitraum beginnt an ihm, damit er in der Liste steht.
   */
  readonly tag?: string;
  /** Der Standort des Sprungs — fehlt er oder gibt es ihn nicht, gilt der Hauptstandort (0147). */
  readonly standort?: string;
  /**
   * DER GEÖFFNETE TAG STEHT IN DER ADRESSE (Auftrag CRM-68).
   *
   * Derselbe Weg wie der Einstieg aus den Terminen: `tag` und `standort`
   * kommen aus der Adresse herein, und wer hier einen Tag öffnet oder
   * schließt, meldet ihn mit diesem Aufruf hinaus. `null` ist die Liste der
   * Tage. `ersetzen` heißt: kein neuer Eintrag im Verlauf — so führt
   * „zurück" vom Tag in die Liste und nicht durch jeden geblätterten Tag.
   */
  readonly beiTag?: (tag: string | null, standort: string, ersetzen: boolean) => void;
  readonly darfPlanen: boolean;
  /** ÄA-04, BUC-138: Die Absage aus dem Terminfenster heraus. */
  readonly darfAbsagen: boolean;
  /** DAT-08: die Exklusivvereinbarung pflegen — `termin/aendern`. */
  readonly darfVereinbarungAendern?: boolean;
  /** BUC-11 (ÄA-55): wie viele Wartende zum Termin passen — `buchung/sehen`. */
  readonly darfWartelisteSehen?: boolean;
  /** CRM-117 (L6): der Weg in die Terminakte; ohne ihn bleibt das Fenster ohne Verweis. */
  readonly beiTerminakte?: (termin: string) => void;
  /** ANG-32: Die Antwort auf eine Terminoption gibt die Disposition. */
  readonly darfOptionBeantworten: boolean;
  /**
   * TAG-33: Wer den Tag freigeben darf — `tagesplan/freigeben` (BEN-02).
   *
   * Ein eigenes Recht neben `planen`: Die Freigabe ist die Zusage, dass der Tag
   * so laeuft. Bei Operations liegt sie, nicht bei jedem, der disponiert.
   */
  readonly darfFreigeben: boolean;
}): JSX.Element {
  const [standorte, setzeStandorte] = useState<readonly Planungsstandort[]>([]);
  const [standort, setzeStandort] = useState(einstiegsort ?? '');
  const [von, setzeVon] = useState(() => einstiegstag ?? heute());
  const [bis, setzeBis] = useState(() => inTagen(einstiegstag ?? heute(), 30));
  const [tage, setzeTage] = useState<readonly Tageszeile[]>([]);
  /*
   * DER TAG, DEN DIE ADRESSE VERLANGT (Auftrag CRM-68), als `JJJJ-MM-TT`.
   *
   * `offen` ist der Betriebstag, dessen Plan geladen ist; `gewuenscht` ist
   * der Kalendertag, um den es geht. Beides fällt auseinander, solange der
   * Plan lädt oder der Tag an diesem Standort keinen Betriebstag hat.
   */
  const [gewuenscht, setzeGewuenscht] = useState<string | null>(einstiegstag ?? null);
  const gewuenschtJetzt = useRef(gewuenscht);
  gewuenschtJetzt.current = gewuenscht;
  const [offen, setzeOffen] = useState<string | null>(null);
  const [plan, setzePlan] = useState<Tagesplan | null>(null);
  /*
   * TAG-39: Am abgeschlossenen Tag bietet die Maske keinen Handgriff mehr an.
   * Der Server weist ohnehin ab (`tag_abgeschlossen`); das Recht selbst
   * bleibt, wie es ist.
   */
  const darfPlanen = darfPlanenRecht && plan?.planungsstand !== 'abgeschlossen';
  /* Auftrag CRM-68: die offenen Tage ALLER Standorte im Zeitraum der Liste. */
  const [offeneJeStandort, setzeOffeneJeStandort] = useState<readonly OffeneTageJeStandort[]>([]);
  /*
   * WOHIN DER FOKUS NACH DEM WECHSEL GEHT (Auftrag CRM-68, WCAG 2.4.3).
   *
   * Beim Öffnen auf die Überschrift des Tages, beim Schließen zurück auf die
   * Zeile, aus der man kam. Ein Ref und kein Zustand: Es ist ein Auftrag an
   * das nächste Bild und nichts, was gezeichnet wird. Das Aufmachen des
   * heutigen Tages beim Einstieg setzt ihn nicht — der Fokus gehört dann der
   * Seite und nicht einer Überschrift mitten darin.
   */
  const fokusNach = useRef<'tag' | 'zeile' | null>(null);
  const zuletztOffen = useRef<string | null>(null);
  const tagesueberschrift = useRef<HTMLHeadingElement>(null);
  const tagesliste = useRef<HTMLTableElement>(null);
  /*
   * DIE PROBE: WELCHE TERMINE GERADE NICHT MITZAEHLEN (TAG-13, TAG-20).
   *
   * Auftraggeber, 13.09.2026: „in der trainingsplanung soll grundsätzlich die
   * möglichkeit bestehen, jeden termin an und abzuwählen und von der planung
   * auszuschließen, damit kann einfach getestet werden, ob die ressourcen
   * reichen."
   *
   * SIE STEHT IM BILDSCHIRM UND NICHT AM BETRIEBSTAG. Was einer zur Probe
   * ausblendet, darf dem Kollegen daneben nicht fehlen — und der Plan selbst
   * ändert sich nicht: Der Server rechnet nur ohne diese Termine.
   */
  const [ausgeblendet, setzeAusgeblendet] = useState<readonly string[]>([]);
  /*
   * DAS TERMINFENSTER (Auftraggeber, 13.09.2026). Dieselbe Auskunft wie in der
   * Terminansicht — wer im Plan auf einen Termin sieht, hat dieselben Fragen.
   */
  const [offenerTermin, setzeOffenenTermin] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  /** TAG-35: Das Setzen dauert; ohne Rueckmeldung klickt man dreimal. */
  const [setzt, setzeSetzt] = useState(false);
  /*
   * DIE ANGEFASSTE BELEGUNG (TAG-08).
   *
   * Kein Ziehen mit der Maus: Anfassen und Ziel wählen sind zwei Klicks, und
   * beide gehen auch mit der Tastatur. Ein Raster mit acht Gruppen und
   * vierzig Blöcken per Ziehen zu bedienen ist auf einem Notebook-Trackpad
   * eine Zumutung — und für ein Vorleseprogramm gar nicht bedienbar.
   */
  const [angefasst, setzeAngefasst] = useState<string | null>(null);
  /*
   * WAS GERADE GEZOGEN WIRD — als Ref und nicht als Zustand.
   *
   * `angefasst` ist Zustand, weil es GEZEICHNET werden muss. Ein Zug braucht
   * daneben etwas, das SOFORT steht: `dragstart` und `drop` sind zwei
   * Ereignisse, und der Abwurf darf nicht davon abhängen, ob React zwischen
   * beiden schon neu gezeichnet hat. Wer das über den Zustand löst, hat einen
   * Fehler, der nur auf langsamen Rechnern auftritt.
   */
  const gezogen = useRef<string | null>(null);
  /** Wo gerade ein Baustein von Hand gesetzt wird: `Block:Termin`. */
  const [setzstelle, setzeSetzstelle] = useState<string | null>(null);
  /*
   * DIE GEWÄHLTE ANSICHT (13.09.2026).
   *
   * Sie steht am Zustand der Maske und nicht im Speicher des Browsers: Wer den
   * Tag wechselt, behält seine Ansicht; wer die Maske verlässt, fängt bei der
   * Vorbelegung an. Eine gemerkte Wahl wäre ein eigener Vorgang mit eigener
   * Frage — je Benutzer oder je Rechner —, und die stellt sich erst, wenn
   * jemand sie stellt.
   */
  const [ansicht, setzeAnsicht] = useState<Ansicht>('zeitachse');
  /*
   * AUF DEM TELEFON NUR LESEN (Auftrag CRM-106, ÄA-63). Unter 600 px steht
   * der Tag als Karten je Termin statt als Zeitachse oder Raster, und keine
   * Handlung wird angeboten. Reine Darstellung: Das Recht `darfPlanen` bleibt,
   * wie es ist, und der Server entscheidet weiter.
   */
  const telefon = useTelefon();
  /** Ob Planungshandlungen ANGEBOTEN werden — das Recht und ein Schirm, der sie trägt. */
  const planenSichtbar = darfPlanen && !telefon;
  /*
   * WAS ZUR ANGEFASSTEN BELEGUNG PASST (AeA-05, AeA-06).
   *
   * Geholt wird es erst beim Anfassen und nicht mit dem Tag: Der Vorschlag
   * hängt an der einzelnen Übung — welches Modul ihren Baustein trägt, was in
   * ihren Blöcken schon belegt ist, was gesperrt ist. Für vierzig Belegungen
   * im Voraus wären das vierzig Abfragen für neununddreißig Antworten, die
   * niemand liest.
   */
  const [vorschlaege, setzeVorschlaege] = useState<readonly Ressourcenvorschlag[]>([]);
  /*
   * WELCHER BEDARF gerade gedeckt wird (PRD-29).
   *
   * Der Auftraggeber: „Die Ressourcen müssen auf Basis der Bausteine geplant
   * werden." Also wird nicht mehr „eine Ressource" zugeteilt, sondern EIN
   * BEDARF gedeckt — und der Vorschlag beantwortet genau diesen.
   */
  const [offenerBedarf, setzeOffenenBedarf] = useState<string | null>(null);
  /*
   * WAS DIE AUTOMATISCHE ZUTEILUNG BERICHTET (TAG-82).
   *
   * `null` heißt: Sie lief noch nicht. Ein leeres Feld heißt: Sie lief und
   * fand nichts zu tun — das ist eine andere Auskunft und muss auch anders
   * aussehen.
   */
  const [zuteilung, setzeZuteilung] = useState<readonly Zuteilung[] | null>(null);
  const [teiltZu, setzeTeiltZu] = useState(false);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const s = await ladeStandorte();
        setzeStandorte(s.standorte);
        /*
         * DIE PLANUNG GEHT AM HAUPTSTANDORT AUF (0147).
         *
         * Vorher stand hier der erste der Liste, und die Liste ist nach
         * Bezeichnung sortiert — damit begann die Planung auf dem
         * Contidrom, einem fremden Gelände mit einzelnen Trainings.
         *
         * WELCHER der Hauptstandort ist, entscheidet die Datenbank und nicht
         * diese Zeile: `hauptstandort` steht am Standort, weil es ein
         * Stammdatum ist (SYS-10). Trägt ihn keiner, bleibt es beim ersten
         * der Liste — ein Betrieb mit lauter gleichrangigen Standorten hat
         * keinen Hauptstandort, und dann ist die Bezeichnung die einzige
         * Ordnung, die es gibt.
         */
        const anfang =
          s.standorte.find((x) => x.hauptstandort)?.kennung ?? s.standorte[0]?.kennung ?? '';
        /*
         * Ein Standort aus dem Sprung (Auftrag CRM-67) gilt nur, wenn es ihn
         * gibt — eine veraltete Adresse fiele sonst auf eine leere Auswahl.
         */
        setzeStandort((jetzt) =>
          s.standorte.some((x) => x.kennung === jetzt) ? jetzt : anfang,
        );
      } catch (e) {
        setzeFehler(e instanceof ApiFehler ? e : null);
        if (!(e instanceof ApiFehler)) throw e;
      }
    })();
  }, []);

  const holen = useCallback(async () => {
    if (standort === '') return;
    setzeLaedt(true);
    try {
      const antwort = await ladeTage(standort, von, bis);
      setzeTage(antwort.tage);
      setzeFehler(null);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    } finally {
      setzeLaedt(false);
    }
  }, [standort, von, bis]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * DIE OFFENEN TAGE ALLER STANDORTE (Auftrag CRM-68, TAG-33).
   *
   * Derselbe Zeitraum wie die Liste. Scheitert die Abfrage, fehlt die
   * Übersicht — die Planung des gewählten Standorts geht trotzdem; die
   * Meldung steht dann wie jede andere oben.
   */
  const holeOffene = useCallback(async () => {
    try {
      setzeOffeneJeStandort((await ladeOffeneTage(von, bis)).standorte);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }, [von, bis]);

  useEffect(() => {
    void holeOffene();
  }, [holeOffene]);

  /**
   * Liste und Übersicht still nachziehen — nach einem Wechsel des Stands
   * (TAG-33). Ohne „Wird geladen …": Der Tag bleibt dabei offen.
   */
  async function nachziehen(): Promise<void> {
    try {
      if (standort !== '') setzeTage((await ladeTage(standort, von, bis)).tage);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
    await holeOffene();
  }

  /*
   * DIE PROBE LÄDT NEU (TAG-13, TAG-20).
   *
   * Der Ausschluss wirkt im SERVER — ein Haken muss den Plan deshalb neu
   * holen. Nicht beim ersten Lauf: `oeffne` hat gerade geladen.
   */
  const ersteWahl = useRef(true);
  useEffect(() => {
    if (ersteWahl.current) {
      ersteWahl.current = false;
      return;
    }
    if (offen === null) return;
    void (async () => {
      try {
        setzePlan(await ladeTagesplan(offen, ausgeblendet));
      } catch (e) {
        setzeFehler(e instanceof ApiFehler ? e : null);
        if (!(e instanceof ApiFehler)) throw e;
      }
    })();
    /*
      `offen` gehört bewusst NICHT in die Liste: Den Wechsel des Tages besorgt
      `oeffne`, und ein zweiter Lader daneben holte denselben Plan ein zweites
      Mal. Hier zählt allein, dass sich die Auswahl geändert hat.
    */
  }, [ausgeblendet]);

  /*
   * Welcher Plan zuletzt verlangt wurde. Wer schnell blättert, löst zwei
   * Ladevorgänge aus; der langsamere darf den neueren nicht überschreiben.
   */
  const verlangt = useRef<string | null>(null);
  const oeffne = useCallback(async (id: string) => {
    setzeMeldung(null);
    /*
     * Der Bericht der Zuteilung gilt für den Stand, in dem sie lief. Wer
     * danach von Hand etwas ändert, sieht sonst eine Liste, die von einem Tag
     * erzählt, den es so nicht mehr gibt.
     */
    setzeZuteilung(null);
    verlangt.current = id;
    try {
      const geladen = await ladeTagesplan(id, ausgeblendet);
      if (verlangt.current !== id) return;
      setzePlan(geladen);
      setzeOffen(id);
      setzeFehler(null);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }, [ausgeblendet]);

  /*
   * DER HEUTIGE TAG STEHT OFFEN, SOBALD ES IHN GIBT.
   *
   * Die Maske ging bisher mit einer Liste auf, aus der man erst einen Tag
   * wählen musste — und in neun von zehn Fällen war das der heutige. Wer
   * morgens die Planung aufmacht, plant den Tag, an dem er steht.
   *
   * WANN NICHT: sobald schon ein Tag offen ist, den es in der Liste gibt.
   * Sonst spränge die Maske dem Anwender unter der Hand zurück auf heute,
   * kaum dass er einen anderen Tag geöffnet hat. Nach einem Wechsel des
   * Standorts setzt die Auswahl `offen` auf null — dort greift die Regel also
   * wieder, und das ist richtig: Es ist eine andere Planung.
   *
   * Gibt es für heute keinen Betriebstag, geschieht nichts. Einen anzulegen
   * ist eine Handlung und keine Nebenwirkung des Aufmachens (TAG-01).
   */
  /*
   * Mit einem Einstiegstag (Auftrag CRM-67) gilt dieselbe Regel für DIESEN
   * Tag: Wer im Terminkalender auf den 14. klickt, will den 14. sehen.
   *
   * SEIT AUFTRAG CRM-68 NUR BEIM EINSTIEG. Der geöffnete Tag steht in der
   * Adresse, und `/trainingsplanung` ohne Tag ist die Liste — wer mit
   * „Alle Tage" oder „zurück" dorthin geht, will sie sehen und nicht wieder
   * den heutigen Tag. Der heutige geht deshalb nur beim ersten Laden auf und
   * legt dabei einen Verlaufseintrag an: „zurück" führt dann in die Liste.
   */
  const heuteAufschlagen = useRef(einstiegstag === undefined);
  useEffect(() => {
    if (!heuteAufschlagen.current || laedt || standort === '') return;
    heuteAufschlagen.current = false;
    const heutiger = tage.find((t) => t.tag === heute());
    if (heutiger === undefined) return;
    setzeGewuenscht(heutiger.tag);
    beiTag?.(heutiger.tag, standort, false);
  }, [tage, laedt, standort, beiTag]);

  /** Die Zeile des verlangten Tages — gibt es keine, gibt es hier keinen Betriebstag. */
  const tageszeile = gewuenscht === null ? undefined : tage.find((t) => t.tag === gewuenscht);

  /*
   * DER VERLANGTE TAG WIRD GELADEN, EIN VERLASSENER GESCHLOSSEN (Auftrag
   * CRM-68). Eine Stelle für alle Wege — Knopf, Blättern, Übersicht und
   * „zurück" im Browser. Während die Liste lädt, geschieht nichts: Sie
   * könnte noch die des vorigen Standorts sein.
   */
  useEffect(() => {
    if (laedt) return;
    if (tageszeile === undefined) {
      if (offen !== null) {
        verlangt.current = null;
        setzeOffen(null);
        setzePlan(null);
        setzeAngefasst(null);
        setzeOffenenBedarf(null);
        setzeOffenenTermin(null);
      }
      return;
    }
    if (tageszeile.id !== offen) void oeffne(tageszeile.id);
  }, [laedt, tageszeile, offen, oeffne]);

  /*
   * DIE ADRESSE ÄNDERT SICH VON AUSSEN — „zurück" und „vor" im Browser.
   *
   * Nicht beim ersten Bild: Da gelten `einstiegstag` und `einstiegsort`
   * schon als Anfangswerte. Der Zeitraum rückt nach, wenn der Tag außerhalb
   * liegt, damit er in der Liste steht (wie beim Einstieg aus CRM-67).
   */
  const ersteAdresse = useRef(true);
  useEffect(() => {
    if (ersteAdresse.current) {
      ersteAdresse.current = false;
      return;
    }
    const tag = einstiegstag ?? null;
    if (einstiegsort !== undefined && einstiegsort !== standort) {
      setzeTage([]);
      setzeStandort(einstiegsort);
    }
    if (tag === gewuenschtJetzt.current) return;
    if (tag === null) zuletztOffen.current = gewuenschtJetzt.current;
    fokusNach.current = tag === null ? 'zeile' : 'tag';
    if (tag !== null && (tag < von || tag > bis)) {
      setzeVon(tag);
      setzeBis(inTagen(tag, 30));
    }
    setzeGewuenscht(tag);
  }, [einstiegstag, einstiegsort]);

  /**
   * Einen Tag zeigen oder zur Liste zurück — der Weg aus der Maske heraus.
   *
   * `ort` für den Sprung aus der Übersicht an einen anderen Standort.
   */
  function zeigeTag(tag: string | null, ersetzen: boolean, ort: string = standort): void {
    if (tag === null) zuletztOffen.current = gewuenscht;
    fokusNach.current = tag === null ? 'zeile' : 'tag';
    if (ort !== standort) {
      setzeTage([]);
      setzeStandort(ort);
    }
    setzeGewuenscht(tag);
    beiTag?.(tag, ort, ersetzen);
  }

  /*
   * DER FOKUS FOLGT DEM WECHSEL (WCAG 2.4.3). Auf die Überschrift, sobald
   * der Plan des Tages steht; auf den Knopf der Zeile, sobald die Liste
   * wieder da ist.
   */
  useEffect(() => {
    if (fokusNach.current === 'tag' && plan !== null && plan.id === offen) {
      fokusNach.current = null;
      tagesueberschrift.current?.focus();
    } else if (fokusNach.current === 'zeile' && gewuenscht === null && !laedt) {
      fokusNach.current = null;
      const ziel = zuletztOffen.current;
      if (ziel === null) return;
      tagesliste.current
        ?.querySelector<HTMLButtonElement>(`button[data-tag="${ziel}"]`)
        ?.focus();
    }
  }, [plan, offen, gewuenscht, laedt]);

  /**
   * Der Aushang als PDF (TAG-35). Er öffnet gleich im Betrachter (Auftrag
   * CRM-168) — `ladeUndReiche` steht dafür vor dem ersten `await`.
   *
   * SCHEITERT ER, IST DAS EINE AUSKUNFT UND KEIN ABSTURZ (ERR-08): Der
   * Setzdienst steht in einem eigenen Container. Ist er gerade nicht da,
   * lässt sich der Tag weiter planen — nur eben nicht drucken.
   */
  async function drucke(
    lade: (betriebstag: string) => Promise<{ blob: Blob; dateiname: string }> = ladeAushang,
  ): Promise<void> {
    if (offen === null) return;
    setzeMeldung(null);
    setzeSetzt(true);
    try {
      const hinweis = await ladeUndReiche(() => lade(offen), true);
      if (hinweis !== null) setzeMeldung(hinweis);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    } finally {
      setzeSetzt(false);
    }
  }

  /**
   * TAG-33: den Planungsstand weiterschalten.
   *
   * Die Reihenfolge der Stände steht in der Datenbank; hier steht nur, was der
   * Knopf tut. Was die Freigabe hindert, zeigt die Maske VORHER an — dieser Weg
   * ist der Rückhalt und nicht die Auskunft.
   *
   * Wirft beim Schreiben (CRM-104): Aus der Rückfrage heraus steht der Fehler
   * im Pop-up. `stand` fängt ihn für die Freigabe, die ohne Rückfrage geht.
   */
  async function standSchreiben(neuer: Planungsstand): Promise<void> {
    if (offen === null || plan === null) return;
    setzeMeldung(null);
    setzeSetzt(true);
    try {
      await standSetzen(offen, neuer, plan.fassung);
      await oeffne(offen);
      /* Auftrag CRM-68: Liste und Übersicht nennen den Stand des Tages mit. */
      await nachziehen();
      setzeMeldung(`Der Tag steht jetzt auf „${STANDNAME[neuer]}".`);
    } finally {
      setzeSetzt(false);
    }
  }

  async function stand(neuer: Planungsstand): Promise<void> {
    try {
      await standSchreiben(neuer);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  /** Wirft wie `standSchreiben` — „Ersetzen" ruft es aus der Rückfrage. */
  async function anwendenSchreiben(artikel: string, ersetzen: boolean): Promise<void> {
    if (offen === null) return;
    setzeMeldung(null);
    const { eintraege } = await ablaufAnwenden(offen, artikel, ersetzen, plan?.fassung ?? '');
    await oeffne(offen);
    setzeMeldung(`${String(eintraege)} Planeinträge angelegt.`);
  }

  async function anwenden(artikel: string, ersetzen: boolean): Promise<void> {
    try {
      await anwendenSchreiben(artikel, ersetzen);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  /**
   * TAG-82: die Ressourcen des ganzen Tages auf einmal zuteilen.
   *
   * ==========================================================================
   * OHNE RÜCKFRAGE, WEIL ER NICHTS WEGNIMMT
   * ==========================================================================
   *
   * Der Automat deckt nur, was offen ist; was von Hand steht, fasst er nicht
   * an. Eine Sicherheitsabfrage vor einem Handgriff, der nichts zerstören
   * kann, erzieht dazu, Sicherheitsabfragen wegzuklicken.
   *
   * Der Bericht wird NACH dem Neuladen gesetzt: `oeffne` löscht ihn, und das
   * ist richtig — nur die Reihenfolge muss stimmen.
   */
  async function zuteilen(): Promise<void> {
    if (offen === null) return;
    setzeMeldung(null);
    setzeTeiltZu(true);
    try {
      const { zuteilungen } = await teileRessourcenZu(offen, plan?.fassung ?? '');
      await oeffne(offen);
      setzeZuteilung(zuteilungen);
      setzeAngefasst(null);
      setzeOffenenBedarf(null);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    } finally {
      setzeTeiltZu(false);
    }
  }

  useEffect(() => {
    if (offen === null || angefasst === null || offenerBedarf === null) {
      setzeVorschlaege([]);
      return;
    }
    let verworfen = false;
    ladeVorschlag(offen, angefasst, offenerBedarf)
      .then((a) => {
        if (!verworfen) setzeVorschlaege(a.vorschlaege);
      })
      .catch(() => {
        if (!verworfen) setzeVorschlaege([]);
      });
    return () => {
      verworfen = true;
    };
  }, [offen, angefasst, offenerBedarf]);

  /*
   * ==========================================================================
   * DIE NACHBEARBEITUNG (TAG-08)
   * ==========================================================================
   *
   * Jeder Handgriff lädt den Tag danach neu. Das ist teurer als das Ergebnis
   * an Ort und Stelle einzurechnen — und richtig: Ein Tausch ändert die
   * Hinweise, die Abweichungen und womöglich die Phasenfolge. Wer das in der
   * Maske nachrechnet, führt dieselbe Regel zweimal, und die zweite Fassung
   * läuft irgendwann auseinander.
   *
   * OHNE RÜCKFRAGE (CRM-104, TAG-08): Setzen, Verschieben, Tauschen und das
   * Zuteilen einer Ressource laufen sofort. Der Plan ist „frei
   * nachbearbeitbar", und im Raster wird laufend umgestellt — ein Pop-up je
   * Zug machte das Planen unbedienbar. Nur „Aus dem Plan nehmen" fragt nach;
   * es löscht die Station samt ihren Zuteilungen.
   *
   * Wirft (CRM-104): Aus der Rückfrage heraus steht der Fehler im Pop-up;
   * `handgriff` fängt ihn für die Züge im Raster und schreibt ihn auf die
   * Seite.
   */
  async function handgriffSchreiben(
    tun: () => Promise<unknown>,
    sagen: (ergebnis: unknown) => string,
    /*
     * Beim Zuteilen einer Ressource bleibt die Übung angefasst: Wer einen
     * Trainer setzt, setzt gleich danach den Raum. Beim Verschieben und
     * Tauschen wird losgelassen — dort ist der Handgriff zu Ende.
     */
    losLassen = true,
  ): Promise<void> {
    if (offen === null) return;
    setzeMeldung(null);
    let ergebnis: unknown;
    try {
      ergebnis = await tun();
    } catch (e) {
      /*
       * BEI ERKANNTER GLEICHZEITIGKEIT WIRD NEU GELADEN (TAG-30, ERR-13).
       *
       * Die Meldung allein liesse den Planer auf einem Stand sitzen, den es
       * nicht mehr gibt — sein naechster Handgriff waere dieselbe Abweisung.
       * Was der andere geschrieben hat, steht danach auf dem Bildschirm, und
       * der Fehler bleibt daneben stehen: Wer etwas verloren hat, soll es
       * lesen.
       */
      /* Ebenso am inzwischen abgeschlossenen Tag (TAG-39): Der neue Stand sperrt die Handgriffe. */
      if (
        e instanceof ApiFehler &&
        (e.kennung === 'gleichzeitige_bearbeitung' || e.kennung === 'tag_abgeschlossen')
      ) {
        try {
          setzePlan(await ladeTagesplan(offen, ausgeblendet));
        } catch {
          /* Der zweite Fehler ueberschreibt den ersten nicht. */
        }
      }
      throw e;
    }
    await oeffne(offen);
    if (losLassen) {
      setzeAngefasst(null);
      setzeOffenenBedarf(null);
    }
    setzeSetzstelle(null);
    setzeMeldung(sagen(ergebnis));
  }

  async function handgriff(
    tun: () => Promise<unknown>,
    sagen: (ergebnis: unknown) => string,
    losLassen = true,
  ): Promise<void> {
    try {
      await handgriffSchreiben(tun, sagen, losLassen);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  /**
   * Ein Klick auf eine belegte Zelle.
   *
   * Nichts angefasst → anfassen. Dieselbe noch einmal → loslassen. Eine
   * andere desselben Termins → tauschen. Ein anderer Termin → dort anfassen,
   * denn über Termine hinweg wird nicht getauscht: Was eine Gruppe fährt,
   * geht die andere nichts an (TAG-07).
   */
  /** Eine Station in die Hand nehmen — der eine Griff, den alle Wege teilen. */
  function anfassen(b: Belegung): void {
    setzeAngefasst(b.belegung);
    setzeOffenenBedarf(null);
    setzeSetzstelle(null);
  }

  function beiBelegung(b: Belegung): void {
    if (!darfPlanen) return;
    if (angefasst === null) {
      anfassen(b);
      return;
    }
    if (angefasst === b.belegung) {
      setzeAngefasst(null);
      setzeOffenenBedarf(null);
      return;
    }
    const quelle = plan?.belegungen.find((z) => z.belegung === angefasst);
    if (quelle === undefined || quelle.termin !== b.termin) {
      setzeAngefasst(b.belegung);
      return;
    }
    void handgriff(
      () => tausche(offen ?? '', quelle.belegung, b.belegung, plan?.fassung ?? ''),
      () => `${quelle.bausteinname ?? quelle.aktivitaetsart} und ${b.bausteinname ?? b.aktivitaetsart} getauscht.`,
    );
  }

  /**
   * Ein Klick auf eine freie Zelle.
   *
   * Mit angefasster Belegung desselben Termins → dorthin verschieben. Sonst →
   * die Stelle zum Setzen öffnen.
   */
  function beiFreiemBlock(block: number, termin: string): void {
    if (!darfPlanen) return;
    const quelle = plan?.belegungen.find((z) => z.belegung === angefasst);
    if (quelle !== undefined && quelle.termin === termin) {
      void handgriff(
        () => verschiebe(offen ?? '', quelle.belegung, block, plan?.fassung ?? ''),
        () => `${quelle.bausteinname ?? quelle.aktivitaetsart} verschoben.`,
      );
      return;
    }
    setzeAngefasst(null);
    setzeSetzstelle(`${String(block)}:${termin}`);
  }

  /**
   * Einen Baustein an eine freie Stelle setzen (TAG-08, PRD-29).
   *
   * Beide Ansichten rufen dieselbe Funktion: Die Wahl steht im Raster in einem
   * Feld und auf der Zeitachse in einem Abschnitt, aber was danach geschieht,
   * ist ein Handgriff und kein Nebeneffekt der Darstellung.
   */
  function beiSetzen(termin: string, block: number, baustein: string): void {
    const name = (plan?.setzbar ?? []).find((x) => x.baustein === baustein)?.bezeichnung ?? baustein;
    void handgriff(
      () => setzeBaustein(offen ?? '', termin, block, baustein, plan?.fassung ?? ''),
      () => `${name} eingeplant.`,
    );
  }

  /*
   * ==========================================================================
   * DAS ZIEHEN — DER ZWEITE WEG ZUM VERSCHIEBEN (13.09.2026)
   * ==========================================================================
   *
   * Er tritt neben das Anklicken und ersetzt es nicht: Zwei Klicks gehen mit
   * der Maus, mit dem Finger und mit der Tastatur, ein Zug nur mit der Maus.
   * Beide enden in denselben Handgriffen — `verschieben` und `tauschen` —, es
   * gibt also keinen zweiten Regelsatz und keine zweite Stelle, an der die
   * Regeln von TAG-07 stehen müssten.
   */
  function beiZiehen(b: Belegung): void {
    if (!darfPlanen) return;
    gezogen.current = b.belegung;
    anfassen(b);
  }

  /** Was ein Abwurf meint: die gezogene Station, sonst die angefasste. */
  function gegriffen(): Belegung | null {
    const id = gezogen.current ?? angefasst;
    return id === null ? null : (plan?.belegungen.find((z) => z.belegung === id) ?? null);
  }

  function beiAbwurfFrei(block: number, termin: string): void {
    const quelle = gegriffen();
    gezogen.current = null;
    if (!darfPlanen || quelle === null) return;
    /*
     * ÜBER TERMINE HINWEG WIRD NICHT VERSCHOBEN (TAG-07) — und das steht da.
     * Ein Abwurf, der stillschweigend nichts tut, sieht aus wie ein Fehler der
     * Maske; wer die Regel nicht kennt, versucht es dreimal.
     */
    if (quelle.termin !== termin) {
      setzeMeldung('Über Termine hinweg wird nicht verschoben — was eine Gruppe fährt, geht die andere nichts an.');
      return;
    }
    void handgriff(
      () => verschiebe(offen ?? '', quelle.belegung, block, plan?.fassung ?? ''),
      () => `${quelle.bausteinname ?? quelle.aktivitaetsart} verschoben.`,
    );
  }

  function beiAbwurfAuf(b: Belegung): void {
    const quelle = gegriffen();
    gezogen.current = null;
    if (!darfPlanen || quelle === null || quelle.belegung === b.belegung) return;
    if (quelle.termin !== b.termin) {
      setzeMeldung('Über Termine hinweg wird nicht getauscht — was eine Gruppe fährt, geht die andere nichts an.');
      return;
    }
    void handgriff(
      () => tausche(offen ?? '', quelle.belegung, b.belegung, plan?.fassung ?? ''),
      () =>
        `${quelle.bausteinname ?? quelle.aktivitaetsart} und ${b.bausteinname ?? b.aktivitaetsart} getauscht.`,
    );
  }

  /** Warum ein Training sich nicht anwenden lässt — oder `null`, wenn es geht. */
  function hindernis(t: Trainingszeile): string | null {
    if (t.startblock === null) {
      return `Beginnt um ${t.beginn} — das liegt außerhalb des Betriebstags.`;
    }
    /*
     * SEIT AeA-11 IST DAS DER EINZIGE GRUND.
     *
     * Vorher standen hier drei: kein Ablaufschema, kein Schema für DIESE
     * Terminzahl, und mehrere Läufe am Tag. Alle drei waren Eigenschaften des
     * Schemas und keine des Trainings. Der Ablauf folgt jetzt aus den
     * Bausteinen — bleibt das Produkt ohne Bausteinfolge, und das ist
     * wirklich nichts anzuordnen.
     */
    if (t.bausteine === 0) return 'Keine Trainingsdefinition hinterlegt.';
    return null;
  }

  /*
   * ==========================================================================
   * DER TAG ALS ZEITACHSE (TAG-03, AeA-09)
   * ==========================================================================
   *
   * Der Auftraggeber am 07.09.2026: „Zur Planung eines Tages bietet sich eine
   * Ansicht, ähnlich der Terminansicht an, nur dass dort die Bausteine
   * zugeordnet werden."
   *
   * Die Terminansicht (TAG-29) führt je Training eine Zeile und trägt Anfang
   * und Dauer als Balken auf einer gemeinsamen Stundenachse. Genau das
   * braucht der Tagesplan: **je Termin eine Zeile, die Bausteine als
   * Abschnitte darauf.**
   *
   * Vorher standen die Blöcke als Zeilen und die Termine als Spalten — bei
   * vierzig Blöcken eine Tabelle, durch die man scrollt, um zu sehen, was um
   * halb elf läuft. Die Zeitachse zeigt den Tag am Stück und in der Richtung,
   * in der er gelesen wird.
   *
   * SEIT DEM 13.09.2026 IST SIE EINE VON ZWEI. Was sie nicht kann, ist einen
   * Satz in einen Abschnitt von 7,5 % Breite schreiben — dafür gibt es das
   * Raster. Die Daten sind dieselben, das Modell darunter ist eines.
   */

  const bloecke =
    plan === null ? [] : [...new Set(plan.raster.map((z) => z.block))].sort((a, b) => a - b);
  const ersterBlock = bloecke[0] ?? 1;
  const blockzahl = bloecke.length;

  /** Die Zeit eines Blocks — für die Achse und für die Beschriftung. */
  const blockzeit = new Map((plan?.raster ?? []).map((z) => [z.block, z.beginn]));

  /**
   * Die Abschnitte eines Termins: belegt und frei, in der Reihenfolge des Tages.
   *
   * Beides wird gezeichnet und beides ist ein Knopf — auf der Bahn als
   * Abschnitt, im Raster als Feld. Eine Darstellung, die nur das Belegte zeigt,
   * beantwortet die einzige Frage nicht, die man an einen Tagesplan stellt: wo
   * ist noch Platz (TAG-03). Der Typ steht in `Tagesraster.tsx`, weil beide
   * Ansichten ihn lesen.
   */
  function abschnitteVon(termin: string): readonly Abschnitt[] {
    const belegt = (plan?.belegungen ?? [])
      .filter((b) => b.termin === termin)
      .sort((a, b) => a.vonBlock - b.vonBlock);

    const stuecke: Abschnitt[] = [];
    let zeiger = ersterBlock;
    for (const b of belegt) {
      if (b.vonBlock > zeiger) {
        stuecke.push({ art: 'frei', von: zeiger, laenge: b.vonBlock - zeiger });
      }
      stuecke.push({ art: 'belegt', von: b.vonBlock, laenge: b.laenge, b });
      zeiger = b.bisBlock + 1;
    }
    const ende = ersterBlock + blockzahl;
    if (zeiger < ende) stuecke.push({ art: 'frei', von: zeiger, laenge: ende - zeiger });
    return stuecke;
  }

  /** Anteil an der Bahn — in Prozent, damit die Achse die Breite bestimmt. */
  const anteil = (n: number): string =>
    `${String(Math.round((n / Math.max(blockzahl, 1)) * 10000) / 100)}%`;

  /*
   * DIE MARKEN DER ACHSE: jede volle Stunde, höchstens ein Dutzend.
   *
   * Vierzig Viertelstunden ergäben vierzig Beschriftungen, die übereinander
   * stünden. Gesucht wird deshalb der erste Block jeder vollen Stunde.
   */
  const achsenmarken = bloecke.filter((b) => (blockzeit.get(b) ?? '').endsWith(':00'));

    /** Die Ressourcen EINER Belegung — ein Griff statt einer Suche je Abschnitt. */
  const ressourcenJeBelegung = new Map<string, Belegungsressource[]>();
  for (const r of plan?.ressourcen ?? []) {
    const bisher = ressourcenJeBelegung.get(r.belegung) ?? [];
    ressourcenJeBelegung.set(r.belegung, [...bisher, r]);
  }
  const ressourcenDer = (belegung: string): readonly Belegungsressource[] =>
    ressourcenJeBelegung.get(belegung) ?? [];

  /*
   * WER EINEN TERMIN DEN GANZEN TAG BEGLEITET (TAG-15, TAG-71).
   *
   * Gelesen aus der Ressourcenbelegung — dem Ort, an dem Kapitel 12
   * Ressourcen an Termine bindet. Zugeteilt wird dort und nicht hier: Diese
   * Maske plant den TAG, die Belegung einer Ressource ist ein eigener Vorgang
   * mit eigener Verfügbarkeitsprüfung (RES-14 bis RES-18).
   */
  const belegungJeTermin = new Map<string, Terminbelegung[]>();
  for (const r of plan?.terminbelegungen ?? []) {
    const bisher = belegungJeTermin.get(r.termin) ?? [];
    belegungJeTermin.set(r.termin, [...bisher, r]);
  }
  const begleitetVon = (termin: string): readonly Terminbelegung[] =>
    belegungJeTermin.get(termin) ?? [];

  /** Was der Termin braucht und was davon fehlt (TAG-15, RES-24). */
  const bedarfDesTermins = (termin: string): readonly Terminbedarf[] =>
    (plan?.terminbedarf ?? []).filter((b) => b.termin === termin);

  /** Der Bedarf EINER Belegung — die Arbeitsliste an der angefassten Übung. */
  const bedarfJeBelegung = new Map<string, Bedarfsdeckung[]>();
  for (const d of plan?.deckung ?? []) {
    const bisher = bedarfJeBelegung.get(d.belegung) ?? [];
    bedarfJeBelegung.set(d.belegung, [...bisher, d]);
  }
  const bedarfDer = (belegung: string): readonly Bedarfsdeckung[] =>
    bedarfJeBelegung.get(belegung) ?? [];

  const angefassteBelegung =
    angefasst === null ? null : (plan?.belegungen.find((b) => b.belegung === angefasst) ?? null);

  /*
   * WIE VIELE ZWINGENDE BEDARFSSTELLEN NOCH OFFEN SIND (TAG-82, TAG-24).
   *
   * Gezählt werden STELLEN und nicht Zeilen: Ein Bedarf über zwei Trainer,
   * von denen einer steht, ist eine offene Stelle und nicht keine. Beide
   * Ebenen zusammen — die Übung braucht ihr Modul, der Termin seinen Trainer.
   */
  const luecke = (verlangt: number, gedeckt: number): number => Math.max(verlangt - gedeckt, 0);
  const offeneBedarfe =
    (plan?.deckung ?? [])
      .filter((d) => d.zwingend)
      .reduce((s, d) => s + luecke(d.verlangt, d.zugeteilt), 0)
    + (plan?.terminbedarf ?? [])
      .filter((b) => b.zwingend)
      .reduce((s, b) => s + luecke(b.verlangt, b.gedeckt), 0);

  /*
   * ==========================================================================
   * GLEICHLAUTENDE BEFUNDE STEHEN EINMAL (0175)
   * ==========================================================================
   *
   * Ein Hinweis, der für jeden Termin desselben Programms wiederholt wird,
   * füllt die Liste und sagt sechsmal dasselbe: „verlangt keine
   * Trainerqualifikation" stand am 08.10.2026 sechs Mal da. Gebündelt steht
   * er einmal, mit der Zahl der betroffenen Termine.
   *
   * WELCHE Termine es sind, sagt die Marke an der Terminzeile — das ist die
   * Auskunft, die der Planer braucht, und sie steht dort, wo er arbeitet.
   */
  type Befundzeile = { readonly text: string; readonly termine: readonly string[] };

  function buendel(
    eintraege: readonly { readonly text: string; readonly termin: string | null }[],
  ): readonly Befundzeile[] {
    const je = new Map<string, string[]>();
    for (const e of eintraege) {
      const bisher = je.get(e.text) ?? [];
      if (e.termin !== null && !bisher.includes(e.termin)) bisher.push(e.termin);
      je.set(e.text, bisher);
    }
    return [...je.entries()].map(([text, termine]) => ({ text, termine }));
  }

  const zwingende = buendel(
    (plan?.konflikte ?? [])
      .filter((k) => k.schwere === 'zwingend')
      .map((k) => ({ text: k.befund, termin: k.termin })),
  );
  const hinweisende = buendel([
    ...(plan?.hinweise ?? []).map((h) => ({ text: h.hinweis, termin: h.termin })),
    ...(plan?.konflikte ?? [])
      .filter((k) => k.schwere === 'hinweis')
      .map((k) => ({ text: k.befund, termin: k.termin })),
  ]);

  /** Wie viele Befunde an EINEM Termin hängen (TAG-13, TAG-24). */
  const befundeJeTermin = new Map<string, { zwingend: number; hinweis: number }>();
  for (const k of plan?.konflikte ?? []) {
    if (k.termin === null) continue;
    const bisher = befundeJeTermin.get(k.termin) ?? { zwingend: 0, hinweis: 0 };
    if (k.schwere === 'zwingend') bisher.zwingend += 1;
    else bisher.hinweis += 1;
    befundeJeTermin.set(k.termin, bisher);
  }
  for (const h of plan?.hinweise ?? []) {
    const bisher = befundeJeTermin.get(h.termin) ?? { zwingend: 0, hinweis: 0 };
    bisher.hinweis += 1;
    befundeJeTermin.set(h.termin, bisher);
  }
  const befunde = (termin: string): { zwingend: number; hinweis: number } =>
    befundeJeTermin.get(termin) ?? { zwingend: 0, hinweis: 0 };

  /*
   * ==========================================================================
   * DIE TERMINE DES TAGES — EIN MODELL FÜR BEIDE ANSICHTEN
   * ==========================================================================
   *
   * Geordnet nach Anfangszeit, dann Kennung. Die Kennung als zweiter Schlüssel
   * ist keine Aussage, sondern eine feste Ordnung: Zwei Termine desselben
   * Produkts zur selben Zeit sollen bei jedem Aufruf in derselben Reihenfolge
   * stehen.
   *
   * ES SAGT, WESSEN GRUPPE ES IST. Der Auftraggeber am 08.09.2026: „Statt
   * Gruppennummer Anzeige ob Einzelteilnehmerkurs, bzw. Anzeige des
   * Firmennamens." Hier stand eine gezählte Nummer — „Gruppe 1", „Gruppe 2".
   * Sie unterschied, aber sie sagte nichts. Was der Planer wissen will, steht
   * in BUC-66: Ist das ein OFFENER Termin mit Einzelplatzverkauf, oder gehört
   * er ganz einem Kunden — und wem? `Einzelplatzverkauf` ist der Wortlaut aus
   * BUC-66 und derselbe wie in der Terminmaske; ein Ding, ein Name.
   *
   * ES WIRD EINMAL GEBAUT (13.09.2026). Vorher holte die Zeitachse sich jedes
   * Stück im JSX mit einem eigenen Griff (`befunde(t.termin)` dreimal in vier
   * Zeilen). Bei zwei Ansichten wären das zwei Sätze solcher Griffe — und der
   * zweite hinkte hinterher, sobald einer etwas dazunimmt.
   */
  const terminzeilen: readonly Terminspalte[] = (() => {
    if (plan === null) return [];
    const kopf = [
      ...new Map(
        plan.raster.map((z) => [
          z.termin,
          {
            termin: z.termin,
            artikel: z.artikel,
            produkt: z.produkt,
            artikelnummer: z.artikelnummer,
            ab: z.terminbeginn,
            /*
              Beim Exklusivtermin der Kunde, sonst die Vertriebsform. Fehlt
              beim Exklusivtermin der Name — die Vereinbarung steht noch aus
              und die Herkunft trägt keinen —, bleibt „Exklusivtermin" stehen:
              Die Terminart ist die Auskunft, die immer stimmt.
            */
            wem:
              z.terminart === 'exklusiv'
                ? (z.kundenname ?? 'Exklusivtermin')
                : 'Einzelplatzverkauf',
          },
        ]),
      ).values(),
    ].sort((a, b) => a.ab.localeCompare(b.ab) || a.termin.localeCompare(b.termin));

    return kopf.map((t) => ({
      ...t,
      befunde: befunde(t.termin),
      begleiter: begleitetVon(t.termin),
      /* Nur die Lücken: Was gedeckt ist, ist keine Nachricht (TAG-15, RES-24). */
      fehlbedarf: bedarfDesTermins(t.termin).filter((b) => b.gedeckt < b.verlangt),
      abschnitte: abschnitteVon(t.termin),
      /* TAG-20: zur Probe abgewählt — er steht da, zählt aber nicht mit. */
      probeAus: ausgeblendet.includes(t.termin),
    }));
  })();

  /**
   * Alle Termine des Tages zum An- und Abwählen (TAG-13, TAG-20).
   *
   * AUS `raster` und damit aus derselben Quelle wie die Spalten: Das Raster
   * kommt aus `fach.betriebstag_plan` und trägt den Ausschluss NICHT — der
   * abgewählte Termin bleibt deshalb sichtbar und wieder anwählbar. Käme die
   * Liste aus einer gefilterten Quelle, verschwände mit dem Haken die
   * Möglichkeit, ihn zurückzuholen.
   */
  const alleTermine = [
    ...new Map(
      (plan?.raster ?? []).map((z) => [
        z.termin,
        { termin: z.termin, beschriftung: `${z.terminbeginn} · ${z.produkt}` },
      ]),
    ).values(),
  ].sort((a, b) => a.beschriftung.localeCompare(b.beschriftung));

  /** Die Zeilen der Rasteransicht — je Wechsel eine, nicht je Block. */
  const zeitzeilen = rasterzeilen(terminzeilen, ersterBlock, blockzahl);

  const gemacht = (zuteilung ?? []).filter((z) => z.ergebnis === 'zugeteilt');
  const uebrig = (zuteilung ?? []).filter((z) => z.ergebnis === 'offen');
  /* ÄA-17: Was die Zuteilung GEDREHT hat, bevor sie gesucht hat. */
  const gestaffelt = (zuteilung ?? []).filter((z) => z.ergebnis === 'gestaffelt');

  /*
   * DIE WERKZEUGLEISTE (ÄA-41, Seitentyp Planung): Standort, Zeitraum und die
   * Ansicht. Die Ansicht steht erst da, wenn es etwas zu sehen gibt — ohne
   * Termin gibt es weder Zeitachse noch Raster.
   */
  /*
   * EIN TAG OFFEN = NUR DER TAG (Auftrag CRM-68).
   *
   * Auftraggeber, 06.10.2026: „beim öffnen eines Tages soll die liste der
   * tage verschwinden und nur der einzelne tag angezeigt werden." Die
   * Werkzeugleiste wechselt mit: In der Liste wählt man Standort und
   * Zeitraum, im Tag geht man zurück oder blättert.
   */
  const eintagesansicht = tageszeile !== undefined;
  const stelle = tageszeile === undefined ? -1 : tage.indexOf(tageszeile);
  const vorheriger = stelle > 0 ? tage[stelle - 1] : undefined;
  const naechster = stelle >= 0 ? tage[stelle + 1] : undefined;
  /*
   * DER STANDORT STEHT OBEN (Auftrag CRM-81). Auftraggeber, 06.10.2026:
   * „schreibe den Standort oben beim durchblättern der tage." Aus der
   * Standortliste und nicht aus dem Plan — so steht er auch, solange der Plan
   * des geblätterten Tages noch lädt.
   */
  const standortname =
    standorte.find((s) => s.kennung === standort)?.bezeichnung ?? plan?.standortname ?? standort;

  const werkzeuge = eintagesansicht ? (
    <>
      <button
        type="button"
        className="knopf"
        onClick={() => {
          zeigeTag(null, false);
        }}
      >
        ← Alle Tage
      </button>{' '}
      {/* Als Text vor den Knöpfen — im Lesefluss vor dem Blättern (BAR-02). */}
      <span className="liste__einzeilig">
        <strong>{standortname}</strong> ·{' '}
        {new Date(`${tageszeile.tag}T12:00:00`).toLocaleDateString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })}
      </span>{' '}
      {/*
        BLÄTTERN IN DEN TAGEN DER LISTE — derselbe Standort, derselbe
        Zeitraum. Es ERSETZT den Verlaufseintrag: „zurück" führt in die
        Liste und nicht durch jeden geblätterten Tag.
      */}
      <button
        type="button"
        className="knopf knopf--klein"
        disabled={vorheriger === undefined}
        onClick={() => {
          if (vorheriger !== undefined) zeigeTag(vorheriger.tag, true);
        }}
      >
        ‹ Vorheriger Tag
      </button>{' '}
      <button
        type="button"
        className="knopf knopf--klein"
        disabled={naechster === undefined}
        onClick={() => {
          if (naechster !== undefined) zeigeTag(naechster.tag, true);
        }}
      >
        Nächster Tag ›
      </button>
      {/*
        DIE WAHL DER ANSICHT (13.09.2026, ÄA-41: in der Werkzeugleiste).

        Zwei Wahlen statt einer Auswahlliste — bei zwei Möglichkeiten ist die
        Liste ein Klick mehr für dieselbe Auskunft. Welche gerade gilt, sagt
        `aria-pressed` und nicht nur die Farbe (BAR-02).
      */}
      {/* Auf dem Telefon gibt es nur die Karten — nichts zu wählen (CRM-106). */}
      {plan !== null && terminzeilen.length > 0 && !telefon && (
        <Segmente<Ansicht>
          beschriftung="Ansicht"
          optionen={[
            { wert: 'zeitachse', text: 'Zeitachse' },
            { wert: 'raster', text: 'Raster' },
          ]}
          gewaehlt={ansicht}
          beiWahl={setzeAnsicht}
        />
      )}
    </>
  ) : (
    <>
      <label className="liste__filter">
        Standort{' '}
        <select
          aria-label="Standort"
          value={standort}
          onChange={(e) => {
            const neu = e.target.value;
            /*
              Steht ein Tag in der Adresse, den es am alten Standort nicht
              gab (CRM-67), wird er am neuen gesucht — die Adresse zieht mit.
            */
            if (gewuenscht !== null) {
              zeigeTag(gewuenscht, true, neu);
              return;
            }
            setzeTage([]);
            setzeStandort(neu);
          }}
        >
          {standorte.map((s) => (
            <option key={s.kennung} value={s.kennung}>
              {/*
                Die Zahl der Betriebstage steht in der AUSWAHL. Ein Standort
                ohne Betriebstag hat keinen Tagesplan — die Maske bliebe leer,
                ohne dass jemand sähe, dass das an der Auswahl liegt.
              */}
              {s.bezeichnung}
              {s.betriebstage === 0 ? ' (kein Betriebstag)' : ''}
            </option>
          ))}
        </select>
      </label>{' '}
      <label className="liste__filter">
        von{' '}
        <input
          type="date"
          aria-label="Zeitraum von"
          value={von}
          onChange={(e) => {
            setzeVon(e.target.value);
          }}
        />
      </label>{' '}
      <label className="liste__filter">
        bis{' '}
        <input
          type="date"
          aria-label="Zeitraum bis"
          value={bis}
          onChange={(e) => {
            setzeBis(e.target.value);
          }}
        />
      </label>
    </>
  );

  /*
   * DIE SEITENLEISTE: die angefasste Station (TAG-08, PRD-29).
   *
   * Nur solange etwas in der Hand liegt — sonst gehört die ganze Breite der
   * Zeitachse (TAG-03). Sie klebt oben: Wer weit unten im Plan eine Station
   * anfasst, sieht Bedarf und Handgriffe, ohne zu scrollen.
   */
  const seite =
    planenSichtbar && angefassteBelegung !== null ? (
      <>
        {/*
          DIE LEISTE SAGT, WAS GERADE GILT.

          Ein angefasstes Element ohne sichtbaren Zustand ist der
          häufigste Weg, versehentlich etwas zu verschieben: Man klickt
          irgendwohin und weiß nicht mehr, dass noch etwas in der Hand
          liegt. Deshalb steht es hier — mit dem Weg heraus daneben.
        */}
        <div className="streifen streifen--info" role="status">
          <strong>
            {angefassteBelegung.bausteinname ?? angefassteBelegung.aktivitaetsart}
          </strong>{' '}
          ({angefassteBelegung.produkt}, {angefassteBelegung.vonZeit}–
          {angefassteBelegung.bisZeit}) ist angefasst. Wählen Sie eine freie Stelle zum
          Verschieben oder eine andere Station desselben Termins zum Tauschen.{' '}
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => {
              setzeAngefasst(null);
              setzeOffenenBedarf(null);
            }}
          >
            Loslassen
          </button>{' '}
          <button
            type="button"
            className="knopf knopf--eingriff knopf--klein"
            onClick={() => {
              setzeNachfrage({ art: 'leeren', belegung: angefassteBelegung });
            }}
          >
            Aus dem Plan nehmen …
          </button>
        </div>

        {/*
          WER UND WO (TAG-03, DAT-14).

          TAG-03 verlangt je Zelle Gruppe, Aktivität, Trainer und
          Betriebsmittel. Zugeteilt wird hier, an der angefassten
          Übung — und nicht je Viertelstunde: Ein Trainer gehört zu
          einer 45-Minuten-Übung, nicht zu einem Block.
        */}
        {/*
          WER UND WO — AUF BASIS DES BAUSTEINS (PRD-29, TAG-03).

          Der Auftraggeber am 07.09.2026: „Bei der Tagesplanung werden
          Ressourcen und keine Bausteine geplant. Die Ressourcen
          müssen auf Basis der Bausteine geplant werden."

          Deshalb steht hier nicht mehr eine offene Ressourcenliste,
          sondern DER BEDARF DES BAUSTEINS: eine Zeile je Bedarf, mit
          dem, was er verlangt, und dem, was steht. Wer eine Zeile
          anfasst, bekommt nur Ressourcen, die genau diesen Bedarf
          decken.
        */}
        <div className="knopfreihe">
          {bedarfDer(angefassteBelegung.belegung).length === 0 ? (
            <>
              <strong>Kein Bedarf hinterlegt.</strong>{' '}
              <span className="hilfe">
                Für „{angefassteBelegung.bausteinname ?? angefassteBelegung.aktivitaetsart}"
                ist im Bausteinkatalog nicht gepflegt, was die Übung braucht —
                die Vollständigkeit lässt sich damit nicht prüfen.
              </span>
            </>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Bedarf dieser Übung">
                <thead>
                  <tr>
                    <th scope="col">Braucht</th>
                    <th scope="col">Zugeteilt</th>
                    {darfPlanen && <th scope="col">Handlung</th>}
                  </tr>
                </thead>
                <tbody>
                  {bedarfDer(angefassteBelegung.belegung).map((d) => {
                    const gedeckt = d.zugeteilt >= d.verlangt;
                    /*
                      WAS DIESEN BEDARF DECKT — aus der Antwort und nicht
                      aus der Ressourcenart (0175).
  
                      Vorher stand hier ein Filter auf `d.ressourcenart`.
                      Bei einem Bedarf über eine EIGENSCHAFT („das
                      Bistro", ÄA-15) ist die Art leer: Das Bistro stand
                      am Balken, aber nicht in dieser Zeile — und ließ
                      sich hier nicht wegnehmen.
                    */
                    const passende = ressourcenDer(angefassteBelegung.belegung).filter((r) =>
                      d.ressourcen.includes(r.ressource),
                    );
                    return (
                      <tr key={d.bedarf}>
                        <td>
                          {d.bedarfsname}
                          {d.verlangt > 1 && ` ×${String(d.verlangt)}`}
                          {!d.zwingend && <span className="hilfe"> · freiwillig</span>}
                          {d.vorbereitungMinuten > 0 && (
                            <span className="hilfe">
                              {' '}
                              · {d.vorbereitungMinuten} min Rüstzeit
                            </span>
                          )}
                        </td>
                        <td>
                          {gedeckt ? (
                            darfPlanen ? (
                              /*
                                Wegnehmbar in der Form der Filtermarke (Auftrag CRM-59) —
                                ohne Rückfrage: Das ist Zuteilung, „Zuteilen" setzt sie
                                wieder (CRM-104, TAG-08).
                              */
                              <ul className="liste__marken" aria-label={`Zugeteilt: ${d.bedarfsname}`}>
                                {passende.map((r) => (
                                  <li key={r.ressource}>
                                    <AbwaehlbareMarke
                                      text={r.bezeichnung}
                                      entfernen={() =>
                                        void handgriff(
                                          () =>
                                            ressourceEntfernen(
                                              offen ?? '',
                                              angefassteBelegung.belegung,
                                              r.ressource,
                                              plan?.fassung ?? '',
                                            ),
                                          () => `${r.bezeichnung} entfernt.`,
                                          false,
                                        )
                                      }
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              passende.map((r) => (
                                <span key={r.ressource} className="marke">
                                  {r.bezeichnung}
                                </span>
                              ))
                            )
                          ) : (
                            /* TAG-24, ÄA-41: Was zwingend fehlt, steht in Rost. */
                            <span className={d.zwingend ? 'marke marke--warn' : 'hilfe'}>
                              fehlt ({d.zugeteilt} von {d.verlangt})
                            </span>
                          )}
                        </td>
                        {darfPlanen && (
                          <td>
                            {offenerBedarf === d.bedarf ? (
                              <select
                                aria-label={`Ressource für ${d.bedarfsname}`}
                                value=""
                                onChange={(e) => {
                                  const gewaehlt = vorschlaege.find(
                                    (v) => v.ressource === e.target.value,
                                  );
                                  if (gewaehlt === undefined) return;
                                  void handgriff(
                                    () =>
                                      ressourceSetzen(
                                        offen ?? '',
                                        angefassteBelegung.belegung,
                                        gewaehlt.ressource,
                                        gewaehlt.art,
                                        plan?.fassung ?? '',
                                      ),
                                    () => `${gewaehlt.bezeichnung} zugeteilt.`,
                                    false,
                                  );
                                }}
                              >
                                <option value="">
                                  {vorschlaege.length === 0
                                    ? 'nichts Passendes gefunden'
                                    : 'Ressource wählen …'}
                                </option>
                                {vorschlaege.map((v) => (
                                  <option key={v.ressource} value={v.ressource}>
                                    {v.bezeichnung}
                                    {v.eignungsgrad !== null && ` · ${v.eignungsgrad}`}
                                    {/*
                                      BELEGT UND GESPERRT STEHEN DABEI.
                                      Wer es erst in der Konfliktliste
                                      erfährt, hat die Wahl schon
                                      getroffen.
                                    */}
                                    {v.gesperrt && ' · gesperrt'}
                                    {v.belegtImZeitraum && ' · schon belegt'}
                                    {v.status !== 'aktiv' && ` · ${v.status}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                onClick={() => {
                                  setzeOffenenBedarf(d.bedarf);
                                }}
                              >
                                {gedeckt ? 'Ändern' : 'Zuteilen'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    ) : undefined;

  return (
    /*
     * SEITENTYP PLANUNG (ÄA-41): oben die Werkzeugleiste, darunter der Plan in
     * voller Breite, rechts die angefasste Station. Konflikte und Fristen
     * stehen in der Achtungsfarbe.
     */
    <Planung
      werkzeuge={werkzeuge}
      seitenBeschriftung="Angefasste Station"
      /* Die Zeitachse behält ihre Breite, während gezogen wird (TAG-03). */
      seiteSchwebend
      {...(seite === undefined ? {} : { seite })}
      oben={
        <>
          {fehler !== null && <Meldung fehler={fehler} />}
          {meldung !== null && (
            <p className="hinweiszeile" role="status">
              {meldung}
            </p>
          )}
          {/*
            AUFTRAG CRM-68: die offenen Tage aller Standorte — NUR ÜBER DER
            LISTE (Auftrag CRM-81). Auftraggeber, 06.10.2026: „blende die
            offene Planung mit Standorten in der Tagesansicht aus." Auch
            solange ein verlangter Tag noch gesucht wird, steht sie nicht da —
            sonst blitzte sie beim Aufschlagen des heutigen Tages kurz auf.
            Findet sich der Tag nicht, steht die Liste und mit ihr die
            Übersicht. „← Alle Tage" führt zu ihr zurück.

            Ein Klick führt in die Liste des Standorts, nicht an einen Tag.
            Ist es der eigene und die Liste steht schon, bleibt alles, wie es
            ist — kein zweiter Verlaufseintrag.
          */}
          {(gewuenscht === null || (!laedt && tageszeile === undefined)) && (
            <OffenePlanungen
              standorte={offeneJeStandort}
              gewaehlt={standort}
              bis={bis}
              beiWahl={(ort) => {
                if (ort !== standort || gewuenscht !== null) zeigeTag(null, false, ort);
              }}
            />
          )}
          {laedt ? (
            <p className="hinweiszeile">Wird geladen …</p>
          ) : tage.length === 0 ? (
            <p className="hinweiszeile">
              In diesem Zeitraum gibt es an diesem Standort keinen Betriebstag. Ein Betriebstag
              entsteht mit seinem Raster; ohne ihn hat der Tagesplan keine Blöcke.
            </p>
          ) : gewuenscht !== null && tageszeile === undefined ? (
            /*
              Der Sprung aus den Terminen fand seinen Tag nicht (Auftrag
              CRM-67). Das sagt die Maske, statt still den Zeitraum zu zeigen
              — sonst sähe es aus, als sei der Klick verloren gegangen.
            */
            <p className="hinweiszeile" role="status">
              Am {alsTag(gewuenscht)} gibt es an diesem Standort keinen Betriebstag.
            </p>
          ) : undefined}
        </>
      }
    >
      {!laedt && tage.length > 0 && !eintagesansicht && (
        <div className="karte">
          {/*
            DIE TABELLEN TRAGEN NAMEN. Drei Tabellen ohne Beschriftung sind für
            ein Vorleseprogramm dreimal "Tabelle"; der Produktname steht
            außerdem sowohl in der Trainingsliste als auch über der Rasterspalte.
          */}
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Betriebstage" ref={tagesliste}>
              <thead>
                <tr>
                  <th scope="col">Tag</th>
                  {/*
                    NUR DIE TERMINE (Auftraggeber, 09.09.2026). Daneben stand die
                    Zahl der LÄUFE — Produkt und Anfangszeit —, und der
                    Unterschied zwischen beiden Zahlen war die Auskunft „drei
                    gleiche oder drei verschiedene Trainings". Sie ändert an der
                    Planung nichts: Was zu tun ist, hängt an den Terminen.
                  */}
                  <th scope="col">Termine</th>
                  <th scope="col">Stand</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {tage.map((t) => {
                  /*
                    OFFEN = MIT TERMINEN UND NOCH IN PLANUNG (Auftrag CRM-68,
                    TAG-33). Dieselbe Regel wie in der Übersicht darüber; der
                    Stand steht als Text in der Achtungsmarke, nicht nur farbig.
                  */
                  const offenZuPlanen = t.termineGesamt > 0 && t.planungsstand === 'entwurf';
                  return (
                    /*
                      DIE ZULETZT OFFENE ZEILE IST ZU SEHEN (Auftrag CRM-68).
                      Seit ein geöffneter Tag die Liste ersetzt, steht hier nie
                      ein offener Tag; wer zurückkommt, findet aber die Zeile,
                      aus der er kam — markiert und mit dem Fokus darauf.
                    */
                    <tr
                      key={t.id}
                      className={
                        [
                          /* Ein Tag ohne Termin ist ein leerer Tag — er tritt zurück. */
                          t.termineGesamt === 0 ? 'liste__zeile--blass' : '',
                          t.tag === zuletztOffen.current ? 'liste__zeile--gewaehlt' : '',
                        ]
                          .filter((k) => k !== '')
                          .join(' ') || undefined
                      }
                    >
                      <td className="liste__einzeilig">{new Date(`${t.tag}T12:00:00`).toLocaleDateString('de-DE', {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}</td>
                      <td>{t.termineGesamt}</td>
                      <td>
                        {offenZuPlanen ? (
                          <span className="marke marke--warn">{STANDNAME[t.planungsstand]}</span>
                        ) : (
                          STANDNAME[t.planungsstand]
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          data-tag={t.tag}
                          onClick={() => {
                            zeigeTag(t.tag, false);
                          }}
                        >
                          Öffnen
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/*
        AUF DEM TELEFON NUR LESEN (Auftrag CRM-106, ÄA-63): Die Hülle setzt
        den Hinweis „Bearbeiten ab Tablet" darüber und blendet die Knöpfe der
        Handlungen aus. Über 600 px gibt sie den Tag unverändert zurück.
      */}
      {plan !== null && eintagesansicht && (
        <AbTablet>
          <div className="karte">
            <div className="karte__titel">
              {/* Auftrag CRM-68: Ziel des Fokus beim Öffnen — darum tabIndex -1. */}
              <h2 ref={tagesueberschrift} tabIndex={-1}>
                {new Date(`${plan.tag}T12:00:00`).toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}{' '}
                · {plan.standortname}
              </h2>
              <small>
                {plan.tagesbeginn}–{plan.tagesende} · {plan.blocklaengeMinuten} Minuten je Block
              </small>
              {/*
                DER AUSHANG (TAG-35): „Ausgabe als Aushang für den Standort
                … druckbar und exportierbar."

                Er steht im Kopf der Tageskarte und nicht unten am Raster:
                Wer drucken will, hat den Tag gerade offen und sucht die
                Handlung dort, wo der Tag benannt ist. Sichtbar ist er für
                jeden, der den Plan sehen darf — Drucken ist kein Eingriff.
              */}
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={setzt}
                /* Drucken liest nur — es bleibt auch auf dem Telefon (CRM-106). */
                data-auch-telefon
                onClick={() => void drucke()}
              >
                {setzt ? 'Wird gesetzt …' : 'Aushang als PDF'}
              </button>{' '}
              {/*
                TAG-35, zweiter Ausdruck: ein Blatt je Trainer. Es steht neben
                dem Aushang, weil es dieselbe Frage aus der anderen Richtung
                ist — die Wand zeigt alle, das Blatt zeigt einen.
              */}
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={setzt}
                data-auch-telefon
                onClick={() => void drucke(ladeTrainerplan)}
              >
                Trainerpläne als PDF
              </button>
            </div>

            {/*
              DER PLANUNGSSTAND UND SEIN KNOPF (TAG-33, TAG-24).

              Er steht im Kopf der Tageskarte, weil er die Aussage ÜBER den Tag
              ist — nicht über eine Station darin. Und er steht neben dem
              Aushang: Wer druckt, hat in der Regel gerade freigegeben.

              WAS NICHT GEHT, STEHT DA, BEVOR JEMAND ES VERSUCHT. Das Hindernis
              kommt mit dem Plan (`freigabeHindernis`); der Knopf ist dann
              abgeschaltet und sagt daneben, woran es liegt. Eine Abweisung
              nach dem Klick wäre dieselbe Auskunft — nur später.
            */}
            <div className="knopfreihe">
              {/*
                Der Stand ist eine Auskunft und bleibt auf dem Telefon stehen
                — mit ihm die Reihe, deren Knöpfe die Hülle ausblendet (CRM-106).
              */}
              <span
                data-auch-telefon
                className={
                  plan.planungsstand === 'entwurf'
                    ? 'marke'
                    : plan.planungsstand === 'abgeschlossen'
                      ? 'marke marke--inaktiv'
                      : 'marke marke--gut'
                }
              >
                {STANDNAME[plan.planungsstand]}
              </span>{' '}
              {darfFreigeben && plan.planungsstand === 'entwurf' && (
                <>
                  <button
                    type="button"
                    className="knopf knopf--haupt"
                    disabled={setzt || plan.freigabeHindernis !== null || ausgeblendet.length > 0}
                    onClick={() => void stand('freigegeben')}
                  >
                    Tag freigeben
                  </button>{' '}
                  {plan.freigabeHindernis !== null && (
                    <span className="hilfe">{plan.freigabeHindernis}</span>
                  )}
                  {/* Eine Probe ist kein Tag — freigegeben wird der ganze. */}
                  {ausgeblendet.length > 0 && (
                    <span className="hilfe">
                      Erst alle Termine anwählen: Der Plan rechnet gerade als Probe.
                    </span>
                  )}
                </>
              )}
              {darfFreigeben && plan.planungsstand === 'freigegeben' && (
                <>
                  {/* Kein Weg zurück aus der Durchführung (0176) — darum die Rückfrage (CRM-104). */}
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={setzt}
                    onClick={() => {
                      setzeNachfrage({ art: 'stand', neuer: 'in_durchfuehrung' });
                    }}
                  >
                    Durchführung beginnt …
                  </button>{' '}
                  {/*
                    DIE RÜCKNAHME IST EIN EIGENER KNOPF und kein Umschalter:
                    Sie nimmt dem Tag seine Zusage, und das soll man lesen,
                    bevor man es tut (TAG-34) — seit CRM-104 im Pop-up.
                  */}
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={setzt}
                    onClick={() => {
                      setzeNachfrage({ art: 'stand', neuer: 'entwurf' });
                    }}
                  >
                    Freigabe zurücknehmen …
                  </button>{' '}
                  <span className="hilfe">
                    Änderungen bleiben möglich; die eingeteilten Trainer werden benachrichtigt.
                  </span>
                </>
              )}
              {darfFreigeben && plan.planungsstand === 'in_durchfuehrung' && (
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={setzt}
                  onClick={() => {
                    setzeNachfrage({ art: 'stand', neuer: 'abgeschlossen' });
                  }}
                >
                  Tag abschließen …
                </button>
              )}
            </div>

            {/*
              TAG-13: Hinweise sind keine Konflikte. Ein Tag darf mit einem
              bekannten Verstoß geplant werden, solange er sichtbar ist
              (TAG-20) — also neben dem Plan und nicht hinter einem Knopf.
            */}
            {/*
              ZWINGEND ZUERST UND GETRENNT (TAG-24).

              „Regelverstöße sind nach Schwere unterschieden: zwingend
              (verhindert die Freigabe) und Hinweis (dokumentiert, aber
              zulässig)." In eine Liste geworfen verschwindet der doppelt
              verplante Trainer zwischen sechs Anmerkungen zur Pausenzeit.

              Zwingend heißt NICHT, dass die Eingabe blockiert — TAG-20 ist
              da eindeutig. Es heißt: Der Tag lässt sich damit nicht
              freigeben.
            */}
            {zwingende.length > 0 && (
              <div className="streifen streifen--warn">
                <p>
                  <strong>Zwingende Konflikte — der Tag lässt sich so nicht freigeben:</strong>
                </p>
                <ul>
                  {/*
                    DER SCHLÜSSEL TRÄGT DIE STELLE MIT (0175). Zwei Befunde
                    derselben Art im selben Block — etwa zwei ungedeckte
                    Bedarfe um 14:00 — hatten vorher denselben Schlüssel, und
                    React hielt den zweiten für denselben Eintrag.
                  */}
                  {zwingende.map((z, i) => (
                    <li key={`zwingend-${String(i)}`}>
                      {z.text}
                      {z.termine.length > 1 && (
                        <span className="hilfe"> · betrifft {z.termine.length} Termine</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              TAG-24, ÄA-41: Der Konflikt steht in Rost, der Hinweis auf der
              ruhigen hellblauen Fläche — die Schwere ist auch ohne das Wort
              zu sehen.
            */}
            {hinweisende.length > 0 && (
              <div className="streifen streifen--info">
                <p>
                  <strong>Hinweise zu diesem Tag — kein Hindernis:</strong>
                </p>
                <ul>
                  {hinweisende.map((h, i) => (
                    <li key={`hinweis-${String(i)}`}>
                      {h.text}
                      {h.termine.length > 1 && (
                        <span className="hilfe"> · betrifft {h.termine.length} Termine</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h3>Trainings an diesem Tag</h3>
            {plan.trainings.length === 0 ? (
              <p className="hilfe">An diesem Tag läuft kein Training.</p>
            ) : (
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle" aria-label="Trainings an diesem Tag">
                  <thead>
                    <tr>
                      <th scope="col">Beginn</th>
                      <th scope="col">Training</th>
                      <th scope="col">Block</th>
                      <th scope="col">Vorlage</th>
                      {/* Auf dem Telefon keine leere Handlungsspalte (CRM-106). */}
                      {planenSichtbar && <th scope="col">Handlung</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {plan.trainings.map((t) => {
                      const grund = hindernis(t);
                      return (
                        <tr key={`${t.artikel} ${t.beginn}`}>
                          <td>
                            <strong>{t.beginn}</strong>–{t.ende}
                          </td>
                          <td>
                            {t.produkt} <code className="hilfe">{t.artikelnummer}</code>
                          </td>
                          {/*
                            OHNE DIE TERMINZAHL (Auftraggeber, 09.09.2026). Hier
                            stand, wie viele Termine diesen einen Lauf tragen —
                            drei gleichzeitige Trainings sind drei Termine
                            (E-38). Die Zeile bleibt der Lauf, weil der Ablauf
                            je Lauf angewendet wird; wie viele Gruppen darin
                            fahren, steht im Raster darunter und ändert an
                            dieser Handlung nichts.
                          */}
                          <td>
                            {t.startblock === null ? (
                              <span className="hilfe">außerhalb</span>
                            ) : (
                              t.startblock
                            )}
                          </td>
                          <td>
                            {/*
                              WAS NICHT GEHT, STEHT HIER — nicht in einer
                              Meldung nach dem Klick. Wer zwanzig Trainings
                              durchgeht, will die drei sehen, an denen etwas
                              fehlt.
                            */}
                            {grund !== null ? (
                              <span className="hilfe">{grund}</span>
                            ) : t.ablaufBefunde.length === 0 ? (
                              <span>anwendbar</span>
                            ) : (
                              /*
                                TAG-20: Ein Befund ist kein Hindernis. Er steht
                                trotzdem VOR dem Klick — „zwei Termine an
                                derselben Übung" will man wissen, bevor der Tag
                                steht, und nicht erst aus der Konfliktliste.
                              */
                              <>
                                <span>anwendbar</span>
                                <ul className="hilfe">
                                  {t.ablaufBefunde.map((b) => (
                                    <li key={b}>{b}</li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </td>
                          {planenSichtbar && (
                            <td>
                              {grund === null && (
                                <>
                                  <button
                                    type="button"
                                    className="knopf knopf--klein"
                                    onClick={() => void anwenden(t.artikel, false)}
                                  >
                                    Ablauf anwenden
                                  </button>{' '}
                                  {/*
                                    ERSETZEN IST EIN EIGENER KNOPF. Ein
                                    Anwenden, das einen von Hand nachbearbeiteten
                                    Tag stillschweigend überschreibt, nimmt eine
                                    Arbeit weg, die niemand mehr sieht (TAG-08).
                                    Seit CRM-104 nennt die Rückfrage, was verloren geht.
                                  */}
                                  <button
                                    type="button"
                                    className="knopf knopf--eingriff knopf--klein"
                                    onClick={() => {
                                      setzeNachfrage({ art: 'ersetzen', training: t });
                                    }}
                                  >
                                    Ersetzen …
                                  </button>
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="karte">
            <div className="karte__titel">
              <h2>Der Tagesplan</h2>
              {/*
                „GRUPPEN" STAND HIER NOCH (AeA-09). Die Gruppe ist am 07.09.2026
                entfallen — der Termin IST die Einheit, die durch den Tag läuft.
                Eine Unterzeile, die einen abgeschafften Begriff führt, lehrt
                ihn dem nächsten Leser wieder.
              */}
              <small>Blöcke × Termine über alle Trainings</small>
            </div>

            {/*
              DIE AUTOMATISCHE ZUTEILUNG (TAG-82).

              Sie steht über dem Plan und nicht in einem Menü: Sie ist der
              erste Griff nach dem Anwenden einer Vorlage, und danach der,
              den man nach jeder Änderung wiederholt.

              Der Knopf sagt, was noch offen ist — „Ressourcen zuteilen (7
              offen)" ist eine Auskunft, „Ressourcen zuteilen" allein wäre
              ein Angebot ins Blaue. Steht nichts offen, ist er still da und
              abgeschaltet: Sein Fehlen wäre die schlechtere Auskunft, weil
              niemand sähe, dass es ihn gibt.
            */}
            {planenSichtbar && (
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf"
                  disabled={teiltZu || offeneBedarfe === 0}
                  onClick={() => void zuteilen()}
                >
                  {teiltZu
                    ? 'Wird zugeteilt …'
                    : offeneBedarfe === 0
                      ? 'Ressourcen zuteilen — nichts offen'
                      : `Ressourcen zuteilen (${String(offeneBedarfe)} offen)`}
                </button>{' '}
                <span className="hilfe">
                  Deckt jeden zwingenden Bedarf, für den es genau eine passende, freie und
                  geeignete Ressource gibt. Überschreibt nichts, was von Hand steht, und
                  erzeugt keinen zwingenden Konflikt. Was übrig bleibt, steht danach
                  mit Grund darunter.
                </span>
              </div>
            )}

            {zuteilung !== null && (
              <div className="hinweiskarte">
                <p>
                  <strong>
                    {zuteilung.length === 0
                      ? 'Nichts zu tun — jeder zwingende Bedarf ist gedeckt.'
                      : `${String(gemacht.length)} zugeteilt, ${String(uebrig.length)} offen${
                          gestaffelt.length === 0
                            ? ''
                            : `, ${String(gestaffelt.length)} gestaffelt`
                        }.`}
                  </strong>{' '}
                  <button
                    type="button"
                    className="knopf knopf--verweis"
                    onClick={() => {
                      setzeZuteilung(null);
                    }}
                  >
                    Bericht schließen
                  </button>
                </p>
                {/*
                  DIE DREHUNG STEHT ZUERST (ÄA-17).

                  Sie erklärt, warum die Zuteilungen darunter überhaupt
                  gefunden wurden — und sie ist eine Änderung AM PLAN und
                  nicht nur an seiner Besetzung. Wer sie nicht will, dreht von
                  Hand zurück (TAG-08); still bleiben darf sie nicht.
                */}
                {gestaffelt.length > 0 && (
                  <>
                    <p>
                      <strong>Gestaffelt, damit nicht alle zugleich an derselben Station stehen:</strong>
                    </p>
                    <ul>
                      {gestaffelt.map((z) => (
                        <li key={`staffel-${z.termin}-${z.gegenstand}`}>
                          {z.zeit} {z.bedarfsname} · {z.gegenstand}
                          {z.grund !== null && <span className="hilfe"> — {z.grund}</span>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {gemacht.length > 0 && (
                  <ul>
                    {gemacht.map((z) => (
                      <li key={`${z.ebene}-${z.bedarf}-${z.belegung ?? z.termin}`}>
                        {z.zeit} {z.gegenstand} · {z.bedarfsname}:{' '}
                        <strong>{z.ressourcenname}</strong>
                        {z.grund !== null && <span className="hilfe"> — {z.grund}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {/*
                  DIE LÜCKEN STEHEN UNTEN UND MIT GRUND.

                  „Nichts gefunden" ist keine Auskunft: Ob es die Ressource
                  nicht gibt, ob sie belegt ist oder ob nur die Eignung fehlt,
                  sind drei ganz verschiedene nächste Schritte.
                */}
                {uebrig.length > 0 && (
                  <>
                    <p>
                      <strong>Offen geblieben — hier plant ein Mensch:</strong>
                    </p>
                    <ul>
                      {uebrig.map((z) => (
                        <li key={`${z.ebene}-${z.bedarf}-${z.belegung ?? z.termin}`}>
                          {z.zeit} {z.gegenstand} · {z.bedarfsname}
                          {z.grund !== null && <span className="hilfe"> — {z.grund}</span>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {/*
              WAS DIE TRAINER GEMELDET HABEN (RES-77, TAG-43).

              Sie stehen ÜBER dem Plan und nicht unter ihm: Wer den Tag nach
              seinem Ablauf öffnet, will zuerst wissen, was nicht gelaufen ist
              wie geplant. „Lief wie geplant" steht mit da — es ist die Auskunft,
              dass jemand hingesehen hat, und ihr Fehlen heißt „noch nichts
              gemeldet". Das sind verschiedene Dinge.

              DIE ABRECHNUNGSGRUNDLAGE IST IHR EIGENTLICHER EMPFÄNGER (RES-76)
              und noch nicht gebaut. Bis dahin ist diese Liste der einzige Ort,
              an dem eine Rückmeldung ankommt — sie wegzulassen hieße, den
              Trainer ins Leere schreiben zu lassen.
            */}
            {(plan?.rueckmeldungen ?? []).length > 0 && (
              <div className="hinweiskarte">
                <p>
                  <strong>Rückmeldungen der Trainer:</strong>
                </p>
                <ul>
                  {(plan?.rueckmeldungen ?? []).map((r) => (
                    <li key={r.belegung}>
                      {r.ressourcenname}:{' '}
                      <strong>{RUECKMELDUNGSNAME[r.art] ?? r.art}</strong>
                      {r.vonZeit !== null && (
                        <>
                          {' '}
                          ({r.vonZeit}–{r.bisZeit})
                        </>
                      )}
                      {r.bemerkung !== null && <span className="hilfe"> — {r.bemerkung}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              ZWEI ANSICHTEN DESSELBEN TAGES (13.09.2026). Gewählt wird oben in
              der Werkzeugleiste; hier steht, was sie unterscheidet.
            */}
            {terminzeilen.length > 0 && !telefon && (
              <p className="hilfe">
                Die <strong>Zeitachse</strong> zeigt den Tag am Stück — je Termin eine Bahn,
                die Dauer als Länge. Das <strong>Raster</strong> stellt die Zeit senkrecht und
                gibt jeder Station die ganze Spalte: Baustein, Zeit, Ressourcen und was fehlt,
                als Text statt im Tooltip.
              </p>
            )}

            {/*
              DIE PROBE: TERMINE AB- UND WIEDER ANWÄHLEN (TAG-13, TAG-20).

              Auftraggeber, 13.09.2026: „in der trainingsplanung soll
              grundsätzlich die möglichkeit bestehen, jeden termin an und
              abzuwählen und von der planung auszuschließen, damit kann einfach
              getestet werden, ob die ressourcen reichen."

              ES RECHNET DER SERVER. Die harten Befunde des Tages — „das Modul
              ist zwei Terminen zugeteilt", „das Bistro fasst 40, eingeplant
              sind 52" — gehören dem TAG und keinem Termin; eine Maske, die
              Zeilen ausblendet, kann sie nicht neu rechnen (0198).

              UND SIE SCHREIBT NICHTS. Der Plan bleibt, wie er ist; nur die
              Rechnung lässt die abgewählten Termine weg. Solange etwas
              abgewählt ist, ist die Freigabe gesperrt: Ein Tag, der auf einer
              Probe freigegeben würde, wäre für einen Tag freigegeben, den es
              so nicht gibt.
            */}
            {/*
              Die Probe ist Planungsarbeit — auf dem Telefon steht sie nicht
              (CRM-106). Ein abgewählter Termin trägt in der Karte weiter
              „nicht in der Rechnung".
            */}
            {alleTermine.length > 0 && !telefon && (
              <div className="probe">
                <fieldset className="probe__wahl">
                  <legend className="probe__titel">Termine in der Planung</legend>
                  {alleTermine.map((t) => (
                    <label key={t.termin} className="probe__termin">
                      <input
                        type="checkbox"
                        checked={!ausgeblendet.includes(t.termin)}
                        onChange={(e) => {
                          const an = e.target.checked;
                          setzeAusgeblendet((jetzt) =>
                            an
                              ? jetzt.filter((x) => x !== t.termin)
                              : [...jetzt, t.termin],
                          );
                        }}
                      />{' '}
                      {t.beschriftung}
                    </label>
                  ))}
                </fieldset>
                {ausgeblendet.length > 0 && (
                  <p className="warnung">
                    <strong>
                      {ausgeblendet.length === 1
                        ? 'Ein Termin ist abgewählt'
                        : `${String(ausgeblendet.length)} Termine sind abgewählt`}
                    </strong>{' '}
                    — der Plan rechnet gerade ohne sie. Es ist eine Probe: Gespeichert ist
                    nichts, und die Freigabe bleibt so lange gesperrt.
                  </p>
                )}
              </div>
            )}

            {terminzeilen.length === 0 ? (
              <p className="hilfe">Kein Termin an diesem Tag — es gibt nichts zu verteilen.</p>
            ) : telefon ? (
              /*
                UNTER 600 PX DIE KARTEN (Auftrag CRM-106, ÄA-63, TAG-03). Die
                Zeitachse braucht 22rem Bahn, das Raster eine Spalte je
                Termin; beide leben vom Anfassen. Dieselben Daten, als Liste.
              */
              <Tageskarten
                spalten={terminzeilen}
                beginnVon={(b) => blockzeit.get(b)}
                tagesende={plan.tagesende}
                ressourcenDer={ressourcenDer}
                bedarfDer={bedarfDer}
              />
            ) : ansicht === 'raster' ? (
              <Tagesraster
                spalten={terminzeilen}
                beiTermin={setzeOffenenTermin}
                zeilen={zeitzeilen}
                beginnVon={(b) => blockzeit.get(b)}
                tagesende={plan?.tagesende ?? ''}
                blocklaengeMinuten={plan?.blocklaengeMinuten ?? 15}
                darfPlanen={darfPlanen}
                angefasst={angefasst}
                setzstelle={setzstelle}
                setzbar={plan?.setzbar ?? []}
                ressourcenDer={ressourcenDer}
                bedarfDer={bedarfDer}
                beiBelegung={beiBelegung}
                beiFreiemBlock={beiFreiemBlock}
                beiZiehen={beiZiehen}
                beiAbwurfFrei={beiAbwurfFrei}
                beiAbwurfAuf={beiAbwurfAuf}
                beiSetzen={beiSetzen}
                beiAbbruch={() => {
                  setzeSetzstelle(null);
                }}
              />
            ) : (
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle" aria-label="Tagesplan">
                  <thead>
                    <tr>
                      <th scope="col">Termin</th>
                      <th scope="col">
                        {/*
                          DIE ACHSE STEHT ÜBER ALLEN ZEILEN, EINMAL.
  
                          Je Zeile eine eigene Achse wäre dieselbe Auskunft
                          n-mal — und die Zeilen wären nicht mehr vergleichbar,
                          was der ganze Sinn einer gemeinsamen Achse ist.
                        */}
                        <span className="achse">
                          {achsenmarken.map((nr) => (
                            <span
                              key={nr}
                              className="achse__marke"
                              style={{ marginLeft: nr === achsenmarken[0] ? 0 : undefined }}
                            >
                              {blockzeit.get(nr)}
                            </span>
                          ))}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {terminzeilen.map((t) => (
                      <tr key={t.termin}>
                        <th scope="row">
                          {/* Derselbe Kopf wie ueber der Rasterspalte — eine Stelle,
                              zwei Verwender. Siehe `Tagesraster.tsx`. */}
                          <Terminkopf spalte={t} beiTermin={setzeOffenenTermin} />
                        </th>
                        <td>
                          <span className="balkenbahn">
                            {t.abschnitte.map((a) =>
                              a.art === 'belegt' ? (
                                <button
                                  key={a.b.belegung}
                                  type="button"
                                  className={
                                    a.b.belegung === angefasst
                                      ? 'plan__abschnitt plan__abschnitt--gewaehlt'
                                      : 'plan__abschnitt'
                                  }
                                  style={{ left: anteil(a.von - ersterBlock), width: anteil(a.laenge) }}
                                  aria-pressed={darfPlanen ? a.b.belegung === angefasst : undefined}
                                  disabled={!darfPlanen}
                                  onClick={() => {
                                    beiBelegung(a.b);
                                  }}
                                  title={`${a.b.bausteinname ?? a.b.aktivitaetsart}, ${a.b.vonZeit}–${a.b.bisZeit}`}
                                >
                                  {/*
                                    DIE LÄNGE IST EINE ANZEIGE, KEINE AUSSAGE
                                    (NFA-14, WCAG 1.4.1). Sie ist nicht
                                    vorlesbar, verschwindet bei starker
                                    Vergrößerung und fehlt im Ausdruck — deshalb
                                    trägt der Abschnitt seine Zeit als TEXT.
                                  */}
                                  <span className="balken__text">
                                    {a.b.bausteinname ?? a.b.aktivitaetsart} {a.b.vonZeit}–
                                    {a.b.bisZeit}
                                    {a.b.abweichend && ' · abweichend'}
                                    {ressourcenDer(a.b.belegung).length > 0 &&
                                      ` · ${ressourcenDer(a.b.belegung)
                                        .map((r) => r.bezeichnung)
                                        .join(', ')}`}
                                  </span>
                                </button>
                              ) : setzstelle === `${String(a.von)}:${t.termin}` ? (
                                <span
                                  key={`frei-${String(a.von)}`}
                                  className="plan__abschnitt plan__abschnitt--frei"
                                  style={{ left: anteil(a.von - ersterBlock), width: anteil(a.laenge) }}
                                >
                                  <SetzWahl
                                    bausteine={(plan?.setzbar ?? []).filter(
                                      (x) => x.artikel === t.artikel,
                                    )}
                                    beiWahl={(baustein) => {
                                      beiSetzen(t.termin, a.von, baustein);
                                    }}
                                    beiAbbruch={() => {
                                      setzeSetzstelle(null);
                                    }}
                                  />
                                </span>
                              ) : (
                                <button
                                  key={`frei-${String(a.von)}`}
                                  type="button"
                                  className="plan__abschnitt plan__abschnitt--frei"
                                  style={{ left: anteil(a.von - ersterBlock), width: anteil(a.laenge) }}
                                  disabled={!darfPlanen}
                                  onClick={() => {
                                    beiFreiemBlock(a.von, t.termin);
                                  }}
                                >
                                  <span className="balken__text">
                                    {blockzeit.get(a.von)} frei
                                    {angefassteBelegung !== null &&
                                      angefassteBelegung.termin === t.termin &&
                                      ' — hierhin'}
                                  </span>
                                </button>
                              ),
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {telefon ? (
              <p className="feld__hilfe">
                Je Termin eine Karte, die Stationen in ihrer Reihenfolge. Was{' '}
                <strong>frei</strong> heißt, ist noch zu vergeben.
              </p>
            ) : (
            <p className="feld__hilfe">
              {ansicht === 'raster'
                ? 'Je Termin eine Spalte, die Zeit nach unten. Jede Station trägt ihren Text im Feld.'
                : 'Je Termin eine Zeile, die Bausteine als Abschnitte auf einer gemeinsamen Zeitachse.'}{' '}
              Was <strong>frei</strong> heißt, ist noch zu vergeben — ein Plan, der nur das
              Belegte zeigt, verschweigt genau das.
              {darfPlanen && (
                <>
                  {' '}
                  Eine Station anklicken fasst sie an; ein zweiter Klick auf eine freie Stelle
                  verschiebt sie, einer auf eine andere Station desselben Termins tauscht beide.
                  {/*
                    DAS ZIEHEN STEHT NUR DA, WO ES GILT. Auf der Zeitachse ist ein
                    Abschnitt zu schmal, um ihn zu treffen; es dort anzubieten
                    hieße, zu einem Weg einzuladen, der misslingt.
                  */}
                  {ansicht === 'raster'
                    && ' Mit der Maus geht auch Ziehen und Fallenlassen — auf eine freie'
                      + ' Stelle verschiebt, auf eine andere Station desselben Termins tauscht.'}{' '}
                  Was dabei gegen die Phasen der Trainingsdefinition läuft, steht danach als
                  Hinweis über dem Plan — verhindert wird es nicht.
                </>
              )}
            </p>
            )}
          </div>
        </AbTablet>
      )}

      {/*
        DAS TERMINFENSTER (Auftraggeber, 13.09.2026: „In der terminansicht und
        trainingsplanung soll jeder Termin auswählbar sein").

        Nach einer Absage oder einer Optionsantwort lädt der Plan neu: Beides
        ändert den Tag — ein abgesagter Termin fällt heraus, eine Zusage bringt
        Plätze mit.
      */}
      {offenerTermin !== null && (
        <Terminfenster
          termin={offenerTermin}
          darfAbsagen={darfAbsagen}
          darfOptionBeantworten={darfOptionBeantworten}
          darfVereinbarungAendern={darfVereinbarungAendern}
          darfWartelisteSehen={darfWartelisteSehen}
          {...(beiTerminakte === undefined ? {} : { beiAkte: beiTerminakte })}
          beiSchliessen={() => {
            setzeOffenenTermin(null);
          }}
          beiAenderung={() => {
            if (offen !== null) void oeffne(offen);
          }}
        />
      )}

      {nachfrage !== null && plan !== null && (
        <Planrueckfrage
          nachfrage={nachfrage}
          plan={plan}
          beiBestaetigen={async () => {
            if (nachfrage.art === 'stand') {
              await standSchreiben(nachfrage.neuer);
            } else if (nachfrage.art === 'ersetzen') {
              await anwendenSchreiben(nachfrage.training.artikel, true);
            } else {
              const b = nachfrage.belegung;
              await handgriffSchreiben(
                () => leereBelegung(offen ?? '', b.belegung, plan.fassung),
                () => `${b.bausteinname ?? b.aktivitaetsart} aus dem Plan genommen.`,
              );
            }
            setzeNachfrage(null);
          }}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        />
      )}
    </Planung>
  );
}

/**
 * DIE RÜCKFRAGE DER TRAININGSPLANUNG (Auftrag CRM-104).
 *
 * Sie nennt, was der Plan weiß: wie viele Stationen und Zuteilungen
 * verschwinden, wie viele davon von Hand nachbearbeitet sind (TAG-08), und
 * ob die eingeteilten Trainer davon erfahren — das tun sie nur nach der
 * Freigabe (TAG-34).
 */
function Planrueckfrage({
  nachfrage,
  plan,
  beiBestaetigen,
  beiSchliessen,
}: {
  readonly nachfrage: Nachfrage;
  readonly plan: Tagesplan;
  readonly beiBestaetigen: () => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const tag = `${plan.standortname} · ${alsTag(plan.tag) ?? plan.tag}`;
  const zuteilungen = (belegungen: readonly string[]): number =>
    plan.ressourcen.filter((r) => belegungen.includes(r.belegung)).length;
  const meldet =
    plan.planungsstand === 'entwurf' ? null : (
      <p>
        Der Tag ist {STANDNAME[plan.planungsstand]} — die eingeteilten Trainer und Dienstleister
        werden benachrichtigt.
      </p>
    );

  if (nachfrage.art === 'stand') {
    const { neuer } = nachfrage;
    return (
      <Rueckfrage
        frage={
          neuer === 'abgeschlossen'
            ? 'Tag abschließen?'
            : neuer === 'in_durchfuehrung'
              ? 'Durchführung beginnen?'
              : 'Freigabe zurücknehmen?'
        }
        unterzeile={tag}
        tat={neuer === 'abgeschlossen' ? 'Abschließen' : neuer === 'in_durchfuehrung' ? 'Beginnen' : 'Zurücknehmen'}
        beiBestaetigen={beiBestaetigen}
        beiSchliessen={beiSchliessen}
      >
        {neuer === 'abgeschlossen' ? (
          <p>
            Der Tag steht danach auf „{STANDNAME.abgeschlossen}". Das lässt sich nicht zurücknehmen.
          </p>
        ) : neuer === 'in_durchfuehrung' ? (
          <p>
            Der Tag steht danach auf „{STANDNAME.in_durchfuehrung}". Die Freigabe lässt sich dann
            nicht mehr zurücknehmen — was gelaufen ist, ist gelaufen.
          </p>
        ) : (
          <p>
            Der Tag fällt zurück auf „{STANDNAME.entwurf}" und gilt nicht mehr als zugesagt; ein
            ausgehängter Plan stimmt dann nicht mehr. Änderungen bis zur nächsten Freigabe gehen
            ohne Nachricht an die Trainer.
          </p>
        )}
      </Rueckfrage>
    );
  }

  if (nachfrage.art === 'ersetzen') {
    const t = nachfrage.training;
    const stationen = plan.belegungen.filter((b) => b.artikel === t.artikel);
    const vonHand = stationen.filter((b) => b.abweichend).length;
    const weg = zuteilungen(stationen.map((b) => b.belegung));
    return (
      <Rueckfrage
        frage="Ablauf ersetzen?"
        unterzeile={`${t.produkt} · ${tag}`}
        tat="Ersetzen"
        beiBestaetigen={beiBestaetigen}
        beiSchliessen={beiSchliessen}
      >
        {stationen.length === 0 ? (
          <p>Für dieses Programm steht noch nichts im Plan — der Ablauf wird neu angelegt.</p>
        ) : (
          <p>
            {stationen.length} {stationen.length === 1 ? 'Station' : 'Stationen'} dieses Programms
            werden gelöscht und aus den Bausteinen neu angelegt
            {vonHand > 0 && `; ${String(vonHand)} davon von Hand nachbearbeitet`}
            {weg > 0 && `. ${String(weg)} ${weg === 1 ? 'Zuteilung entfällt' : 'Zuteilungen entfallen'}`}.
            Das lässt sich nicht zurücknehmen.
          </p>
        )}
        {meldet}
      </Rueckfrage>
    );
  }

  const b = nachfrage.belegung;
  const weg = zuteilungen([b.belegung]);
  return (
    <Rueckfrage
      frage="Station aus dem Plan nehmen?"
      unterzeile={`${b.produkt} · ${b.vonZeit}–${b.bisZeit}`}
      tat="Aus dem Plan nehmen"
      beiBestaetigen={beiBestaetigen}
      beiSchliessen={beiSchliessen}
    >
      <p>
        {b.bausteinname ?? b.aktivitaetsart} wird aus dem Plan des Termins genommen, die Stelle ist
        danach frei
        {weg > 0 && `; ${String(weg)} ${weg === 1 ? 'Zuteilung entfällt' : 'Zuteilungen entfallen'}`}.
      </p>
      {meldet}
    </Rueckfrage>
  );
}
